import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import "./ProjectsView.scss";
import "./TimCompactLayout.scss";
import { TIM_COMPACT_CLASS, getTimLayoutScale, timGridOffset } from "./timCompactLayout";
import { attachGridAutoHeight } from "./timGridAutoHeight";
import { withTimCompactTheme } from "./TimCompactThemeProvider";
import "./devExtremeMenuTimerFix";
import { OidcIdentityContext } from "@rsmus/react-auth";
import DataGrid, {
  Column,
  FilterRow,
  MasterDetail,
  Paging,
  Scrolling,
  Selection,
  StateStoring,
  LoadPanel,
  Toolbar,
} from "devextreme-react/data-grid";
import { useRecoilState } from 'recoil';
import { projectsGridStateAtom, entitiesGridStateAtom, deliverablesGridStateAtom } from '../../recoil/store/gridState.store';
import {
  Alert,
  Button,
  ClickAwayListener,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Stack,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
  InputAdornment,
  buttonClasses,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SettingsIcon from "@mui/icons-material/Settings";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import LaunchIcon from '@mui/icons-material/Launch';
import SearchIcon from "@mui/icons-material/Search";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import AssignmentIcon from "@mui/icons-material/Assignment";
import MessageIcon from "@mui/icons-material/Message";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type { VirtualElement } from "@popperjs/core";
import { useBlocker } from "react-router-dom";
import { createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import { calculateGridHeight } from "../../util/Utils";
import { REACT_APP_TIMBASE_URL } from "../../envVariables";
import MessagesDialog from "./MessagesDialog";
import StatusDetailsDialog from "./StatusDetailsDialog";
import ProjectDetailsDialog from "./ProjectDetailsDialog";
import ProjectVisibilityConfirmDialogs from "./ProjectVisibilityConfirmDialogs";
import TasksGrid from "./TasksGrid";
import {
  ProjectsCustomizationDataRequestDto,
  ProjectsCustomizationDataDto,
  ProjectsCustomizationStatusDto,
  ProjectPerson,
  MentionableUser,
} from "../../api-client-nswag/create-sites-client";
import { SnackbarMessage, useSnackbar } from "../../hooks";



const getProjectSettingLabel = (key: string): string => {
  const map: Record<string, string> = {
    EntityId: "Entity ID",
    Entity: "Entity",
    Project: "Project",
    Consent7216: "7216 Consent",
    TaxYear: "Tax Year",
    EngagementManagers: "Engagement Managers",
    Status: "Status",
    StatusDetails: "Status Details",
    Entities: "Entities",
    Deliverables: "Deliverables",
    Task: "Tasks",
  };
  if (map[key]) return map[key];
  return key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
};
type ProjectRow = {
  id: string;
  clientId: number;
  client: string;
  projectDefaultName: string;
  project: string;
  projectCustomName?: string;
  consent7216: "Yes" | "No" | "Mixed";
  taxYear: number | null;
  projectIdMdm?: number;
  projectIdTim?: number;
  engagementManagers: ProjectPerson[];
  status: "New" | "Ready to Start" | "In Progress" | "On Hold" | "Completed";
  visibility: boolean;
  haveUnreadMessage?: boolean;
  haveUnreadNote?: boolean;
  lastMessageCreated?: string;
  lastMessageCreatedBy?: string;
  lastNoteCreated?: string;
  lastNoteCreatedBy?: string;
  entities?: EntityRow[];
  deliverables?: DeliverableRow[];
  hideallentities?: boolean;
  hidealldeliverables?: boolean;
};

type EntityRow = {
  id: string | number;
  entityId: number;
  entity: string;
  legalEntityType?: string;
  consent7216?: "Yes" | "No" | "Mixed";
  imanageUrl?: string;
  visibility: boolean;
};

type DeliverableRow = {
  id: string | number;
  entityId: number;
  entity: string; // Entity Name
  jurisdiction?: string;
  form?: string;
  status?: string;
  internalDueDate?: string; // formatted date
  statutoryDueDate?: string; // formatted date
  extended?: string; // Yes/No
  filingMethod?: string;
  deliveryMethod?: string;
  visibility: boolean;
};

type ProjectNotificationRead = {
  HQSiteId: number;
  ProjectIdMDM: number;
  LastMessageReadUser: string;
  LastMessageReadTime: string;
  LastStatusNoteReadUser: string;
  LastStatusNoteReadTime: string;
};

const getProjectDisplayName = (
  row: Pick<ProjectRow, "project" | "projectCustomName">,
) => {
  const custom = (row.projectCustomName || "").trim();
  return custom.length > 0 ? custom : row.project;
};

const buildProjectRowSig = (r: ProjectRow) => {
  const mgrSig = (r.engagementManagers || []).map((m) => `${m?.id ?? ""}|${m?.email ?? ""}|${m?.name ?? ""}|${m?.visibility ?? ""}`).join(",");
  return [r.id, r.clientId, r.client, r.project, r.projectCustomName || "", r.consent7216, r.taxYear ?? "", r.projectIdMdm ?? "", r.projectIdTim ?? "", r.status, r.visibility, r.hideallentities ?? "", r.hidealldeliverables ?? "", r.lastMessageCreated ?? "", r.lastMessageCreatedBy ?? "", r.lastNoteCreated ?? "", r.lastNoteCreatedBy ?? "", mgrSig].join("::");
};

const GRID_COLUMN_NAMES = [
  "clientId",
  "client",
  "project",
  "consent7216",
  "taxYear",
  "engagementManagers",
  "status",
  "unread",
  "actions",
];

const CLIENT_SETTING_TO_COLUMN_NAME_MAP: Record<string, string> = {
  EntityId: "clientId",
  Entity: "client",
  Project: "project",
  Consent7216: "consent7216",
  TaxYear: "taxYear",
  EngagementManagers: "engagementManagers",
  Status: "status",
};

const ALL_CLIENT_SETTING_COLUMNS = Array.from(new Set(Object.values(CLIENT_SETTING_TO_COLUMN_NAME_MAP)));

// Pessimistic default: hide all setting-driven columns until settings load.
const DEFAULT_CLIENT_HIDDEN_COLUMNS = ALL_CLIENT_SETTING_COLUMNS;
// Internal-only columns; hidden in client view regardless of project settings.
const CLIENT_VIEW_ALWAYS_HIDDEN_COLUMNS = ["clientId", "visibility"];
const PROJECTS_PAGE_SIZE = 50;
// Bump when the persisted grid-state shape changes, so stale Recoil/sessionStorage
// data from a previous version is discarded instead of silently misapplied.
const GRID_STATE_SCHEMA_VERSION = 3;
const SUBGRID_PAGE_SIZE = 50;
const NESTED_GRID_HEIGHT = 280;
const nestedGridMaxHeight = () => Math.round(NESTED_GRID_HEIGHT * getTimLayoutScale());
const TIM_PROJECT_NOTIFICATION_STORAGE_KEY = "tim_projects_notification_reads";
const SHOW_COLUMN_CHOOSER_BUTTON = true;

const buttonSizing = { [`& .${buttonClasses.startIcon} > *:nth-of-type(1)`]: { fontSize: "35px" } };

type EntitiesDetailGridProps = {
  isClientView: boolean;
  hideAllEntitiesState: boolean;
  loadingEntities: boolean;
  displayEnts: EntityRow[];
  projectId: string;
  handleBulkEntityVisibilityToggle: (projectId: string) => void;
  subViewCellRender: (cell: any) => React.ReactNode;
  ensureEntitiesLoaded: (projectId: string, append?: boolean) => Promise<void>;
  entityLoadingMore: boolean;
  canLoadMoreEntities: boolean;
  onGridInstanceReady?: (projectId: string, instance: any) => void;
};

const EntitiesDetailGrid: React.FC<EntitiesDetailGridProps> = ({
  isClientView,
  hideAllEntitiesState,
  loadingEntities,
  displayEnts,
  projectId,
  handleBulkEntityVisibilityToggle,
  subViewCellRender,
  ensureEntitiesLoaded,
  entityLoadingMore,
  canLoadMoreEntities,
  onGridInstanceReady,
}) => {
  const [entitiesGridState, setEntitiesGridState] = useRecoilState(entitiesGridStateAtom);
  const gridRef = useRef<any>(null);
  const displayEntsRef = useRef<EntityRow[]>(displayEnts);
  displayEntsRef.current = displayEnts;

  const entitiesDataGrid = useMemo(() => (
  <DataGrid
    ref={gridRef}
    keyExpr="id"
    onInitialized={(e: any) => { onGridInstanceReady?.(projectId, e.component); attachGridAutoHeight(e.component, nestedGridMaxHeight); }}
    dataSource={displayEntsRef.current}
    showBorders={false}
    showColumnLines={false}
    showRowLines={true}
    repaintChangesOnly={true}
    rowAlternationEnabled={false}
    noDataText={loadingEntities ? "Loading entities..." : displayEnts.length === 0 ? "No Entities" : ""}
  >
    <StateStoring
      enabled={true}
      type="custom"
      storageKey="entitiesGrid"
      customLoad={() => Promise.resolve(entitiesGridState ? JSON.parse(JSON.stringify(entitiesGridState)) : null)}
      customSave={(state: any) => { try { setEntitiesGridState(state); } catch {} ; return Promise.resolve(); }}
    />
    <LoadPanel enabled={false} />
    <Selection mode="none" />
    <Paging enabled={false} />
    <Scrolling mode="standard" columnRenderingMode="standard" />
    <FilterRow visible={true} showOperationChooser={true} />
    {!isClientView && <Column dataField="entityId" caption="Entity ID" minWidth={120} alignment="left" />}
    <Column dataField="entity" caption="Entity" minWidth={240} alignment="left" />
    <Column dataField="legalEntityType" caption="Legal Entity Type" minWidth={160} alignment="left" />
    {!isClientView && <Column dataField="consent7216" caption="7216 Consent" minWidth={140} alignment="left" />}
    {!isClientView && (
      <Column
        dataField="imanageUrl"
        caption="iManage"
        minWidth={140}
        alignment="left"
        cellRender={(cell) => {
          const url = cell.data.imanageUrl;
          return url ? (
            <a href={url} target="_blank" rel="noreferrer" className="imanage-link">
              iManage <LaunchIcon sx={{ fontSize: 16 }} />
            </a>
          ) : (
            <span className="manage-link-disabled">Manage</span>
          );
        }}
      />
    )}

    {!isClientView && <Column dataField="visibility" caption="Visibility" cssClass="visibility-column" minWidth={120} alignment="center" cellRender={subViewCellRender} />}
  </DataGrid>
), [displayEnts.length, subViewCellRender, isClientView, loadingEntities, projectId]);

  return (
    <div className="nested-grid">
      {!isClientView && (
        <div className="nested-toolbar">
          <Button variant="outlined" size="small" onClick={() => handleBulkEntityVisibilityToggle(projectId)}
            startIcon={
              hideAllEntitiesState ? <VisibilityIcon /> : <VisibilityOffIcon />
            }
          >
            {hideAllEntitiesState ? "Show All to Client" : "Hide All To Client"}
          </Button>
          {loadingEntities && (
            <Stack direction="row" sx={{ ml: 1, alignItems: "center" }}>
              <CircularProgress size={14} sx={{ mr: 1, color: "#00a3e0" }} />
              <span>Refreshing…</span>
            </Stack>
          )}
        </div>
      )}

      <div className="nested-grid-card">{entitiesDataGrid}</div>
      <div className="subgrid-load-more">
        <Button
          variant="text"
          className="load-more-link"
          onClick={() => ensureEntitiesLoaded(projectId, true)}
          disabled={
            loadingEntities ||
            entityLoadingMore ||
            !canLoadMoreEntities
          }
        >
          {entityLoadingMore || loadingEntities
            ? "Loading..."
            : `Load the next ${SUBGRID_PAGE_SIZE} records`}
        </Button>
      </div>
    </div>
  );
};

type DeliverablesDetailGridProps = {
  isClientView: boolean;
  hideAllDeliverablesState: boolean;
  loadingDeliverables: boolean;
  displayDels: DeliverableRow[];
  projectId: string;
  handleBulkDeliverableVisibilityToggle: (projectId: string) => void;
  subViewCellRender: (cell: any) => React.ReactNode;
  ensureDeliverablesLoaded: (projectId: string, append?: boolean) => Promise<void>;
  deliverableLoadingMore: boolean;
  canLoadMoreDeliverables: boolean;
  onGridInstanceReady?: (projectId: string, instance: any) => void;
};

const DeliverablesDetailGrid: React.FC<DeliverablesDetailGridProps> = ({
  isClientView,
  hideAllDeliverablesState,
  loadingDeliverables,
  displayDels,
  projectId,
  handleBulkDeliverableVisibilityToggle,
  subViewCellRender,
  ensureDeliverablesLoaded,
  deliverableLoadingMore,
  canLoadMoreDeliverables,
  onGridInstanceReady,
}) => {
  const [deliverablesGridState, setDeliverablesGridState] = useRecoilState(deliverablesGridStateAtom);
  const gridRef = useRef<any>(null);
  const displayDelsRef = useRef<DeliverableRow[]>(displayDels);
  displayDelsRef.current = displayDels;

  const deliverablesDataGrid = useMemo(() => (
  <DataGrid
    ref={gridRef}
    keyExpr="id"
    onInitialized={(e: any) => { onGridInstanceReady?.(projectId, e.component); attachGridAutoHeight(e.component, nestedGridMaxHeight); }}
    dataSource={displayDelsRef.current}
    showBorders={false}
    showColumnLines={false}
    showRowLines={true}
    repaintChangesOnly={true}
    rowAlternationEnabled={false}
    noDataText={loadingDeliverables ? "Loading deliverables..." : displayDels.length === 0 ? "No Deliverables" : ""}
  >
    <StateStoring
      enabled={true}
      type="custom"
      storageKey="deliverablesGrid"
      customLoad={() => Promise.resolve(deliverablesGridState ? JSON.parse(JSON.stringify(deliverablesGridState)) : null)}
      customSave={(state: any) => { try { setDeliverablesGridState(state); } catch {} ; return Promise.resolve(); }}
    />
    <LoadPanel enabled={false} />
    <Selection mode="none" />
    <Paging enabled={false} />
    <Scrolling mode="standard" columnRenderingMode="standard" />
    <FilterRow visible={true} showOperationChooser={true} />
    {!isClientView && <Column dataField="entityId" caption="Entity ID" minWidth={85} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" />}
    <Column dataField="entity" caption="Entity" minWidth={120} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" />
    <Column dataField="jurisdiction" caption="Jurisdiction" minWidth={105} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" />
    <Column dataField="form" caption="Form" minWidth={75} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" />
    <Column dataField="status" caption="Status" minWidth={90} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" />
    {!isClientView && <Column dataField="internalDueDate" caption="Internal Due Date" minWidth={110} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" />}
    <Column dataField="statutoryDueDate" caption="Statutory Due Date" minWidth={115} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" />
    <Column dataField="extended" caption="Extended" minWidth={80} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" />
    <Column dataField="filingMethod" caption="Filing Method" minWidth={100} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" />
    <Column dataField="deliveryMethod" caption="Delivery Method" minWidth={100} alignment="left" dataType="string" filterOperations={["contains", "notcontains"]} selectedFilterOperation="contains" />
    {!isClientView && <Column dataField="visibility" caption="Visibility" cssClass="visibility-column" minWidth={85} alignment="center" cellRender={subViewCellRender} />}
  </DataGrid>
), [displayDels.length, subViewCellRender, isClientView, loadingDeliverables, projectId]);

  return (
    <div className="nested-grid">
      {!isClientView && (
        <div className="nested-toolbar">
          <Button variant="outlined" size="small" onClick={() => handleBulkDeliverableVisibilityToggle(projectId)}
            startIcon={
              hideAllDeliverablesState ? <VisibilityIcon /> : <VisibilityOffIcon />
            }
          >
            {hideAllDeliverablesState
              ? "Show All to Client"
              : "Hide All from Client"}
          </Button>
          {loadingDeliverables && (
            <Stack direction="row" sx={{ ml: 1, alignItems: "center" }}>
              <CircularProgress size={14} sx={{ mr: 1, color: "#00a3e0" }} />
              <span>Refreshing…</span>
            </Stack>
          )}
        </div>
      )}

      <div className="nested-grid-card">{deliverablesDataGrid}</div>
      <div className="subgrid-load-more">
        <Button variant="text" className="load-more-link" onClick={() => ensureDeliverablesLoaded(projectId, true)}
          disabled={
            loadingDeliverables ||
            deliverableLoadingMore ||
            !canLoadMoreDeliverables
          }
        >
          {deliverableLoadingMore || loadingDeliverables              
            ? "Loading..."
            : `Load the next ${SUBGRID_PAGE_SIZE} records`}
        </Button>
      </div>
    </div>
  );
};

type ProjectDetailPanelProps = {
  projectId: string;
  isClientViewRef: React.MutableRefObject<boolean>;
  clientDetailsEnabledRef: React.MutableRefObject<{ entities: boolean; deliverables: boolean; tasks: boolean }>;
  rowsRef: React.MutableRefObject<ProjectRow[]>;
  detailTabByProjectRef: React.MutableRefObject<Record<string, "entities" | "deliverables">>;
  entityLoadingByProjectRef: React.MutableRefObject<Record<string, boolean>>;
  deliverableLoadingByProjectRef: React.MutableRefObject<Record<string, boolean>>;
  entityTotalByProjectRef: React.MutableRefObject<Record<string, number>>;
  deliverableTotalByProjectRef: React.MutableRefObject<Record<string, number>>;
  entityLastFetchCountByProjectRef: React.MutableRefObject<Record<string, number>>;
  deliverableLastFetchCountByProjectRef: React.MutableRefObject<Record<string, number>>;
  entityLoadingMoreByProjectRef: React.MutableRefObject<Record<string, boolean>>;
  deliverableLoadingMoreByProjectRef: React.MutableRefObject<Record<string, boolean>>;
  subViewCellRender: (
    kind: "entities" | "deliverables",
    projectId: string,
    toggleFn: (projectId: string, id: number | string, checked: boolean) => void,
  ) => (cell: any) => React.ReactNode;
  handleEntityVisibilityToggle: (projectId: string, entityId: number | string, checked: boolean) => void;
  handleDeliverableVisibilityToggle: (projectId: string, deliverableId: number | string, checked: boolean) => void;
  handleBulkEntityVisibilityToggle: (projectId: string) => void;
  handleBulkDeliverableVisibilityToggle: (projectId: string) => void;
  ensureEntitiesLoaded: (projectId: string, append?: boolean) => Promise<void>;
  ensureDeliverablesLoaded: (projectId: string, append?: boolean) => Promise<void>;
  registerEntityGridInstance: (projectId: string, instance: any) => void;
  registerDeliverableGridInstance: (projectId: string, instance: any) => void;
  onTabChange: (projectId: string, tab: "entities" | "deliverables") => void;
  registerUpdater: (projectId: string, fn: (() => void) | null) => void;
};

// Mounted once when a project row expands and never re-created by DevExtreme afterwards.
// All later updates (API loads, tab switches, visibility toggles) are delivered via
// `registerUpdater`'s force-render function rather than a DevExtreme repaintRows call,
// so the master-detail row's DOM is never destroyed/recreated - only this component's
// own React output changes, eliminating the redraw glitch on data load / tab switch.
const ProjectDetailPanel: React.FC<ProjectDetailPanelProps> = ({
  projectId,
  isClientViewRef,
  clientDetailsEnabledRef,
  rowsRef,
  detailTabByProjectRef,
  entityLoadingByProjectRef,
  deliverableLoadingByProjectRef,
  entityTotalByProjectRef,
  deliverableTotalByProjectRef,
  entityLastFetchCountByProjectRef,
  deliverableLastFetchCountByProjectRef,
  entityLoadingMoreByProjectRef,
  deliverableLoadingMoreByProjectRef,
  subViewCellRender,
  handleEntityVisibilityToggle,
  handleDeliverableVisibilityToggle,
  handleBulkEntityVisibilityToggle,
  handleBulkDeliverableVisibilityToggle,
  ensureEntitiesLoaded,
  ensureDeliverablesLoaded,
  registerEntityGridInstance,
  registerDeliverableGridInstance,
  onTabChange,
  registerUpdater,
}) => {
  const [, forceRender] = useReducer((c: number) => c + 1, 0);

  useEffect(() => {
    registerUpdater(projectId, () => forceRender());
    return () => registerUpdater(projectId, null);
  }, [projectId, registerUpdater]);

  const isClientView = isClientViewRef.current;
  const clientDetailsEnabled = clientDetailsEnabledRef.current;
  const row = rowsRef.current.find((r) => r.id === projectId);
  if (!row) return null;

  const currentTab = detailTabByProjectRef.current[projectId] || "entities";
  const entsDefined = row.entities !== undefined;
  const delsDefined = row.deliverables !== undefined;
  const ents: EntityRow[] = row.entities || [];
  const dels: DeliverableRow[] = row.deliverables || [];

  const displayEnts = isClientView ? ents.filter((e) => e.visibility) : ents;
  const displayDels = isClientView ? dels.filter((d) => d.visibility) : dels;

  const loadingEntities = !entsDefined || !!entityLoadingByProjectRef.current[projectId];
  const loadingDeliverables = !delsDefined || !!deliverableLoadingByProjectRef.current[projectId];
  const totalEntities = entityTotalByProjectRef.current[projectId] ?? ents.length;
  const totalDeliverables = deliverableTotalByProjectRef.current[projectId] ?? dels.length;
  const hasMoreEntities = ents.length < totalEntities;
  const hasMoreDeliverables = dels.length < totalDeliverables;
  const lastEntityFetchCount = entityLastFetchCountByProjectRef.current[projectId] ?? 0;
  const lastDeliverableFetchCount = deliverableLastFetchCountByProjectRef.current[projectId] ?? 0;
  const canLoadMoreEntities = hasMoreEntities && lastEntityFetchCount === SUBGRID_PAGE_SIZE;
  const canLoadMoreDeliverables = hasMoreDeliverables && lastDeliverableFetchCount === SUBGRID_PAGE_SIZE;

  const hideAllEntitiesState = !!row.hideallentities;
  const hideAllDeliverablesState = !!row.hidealldeliverables;

  const entityCellRender = subViewCellRender("entities", projectId, handleEntityVisibilityToggle);
  const deliverableCellRender = subViewCellRender("deliverables", projectId, handleDeliverableVisibilityToggle);

  const showEntitiesTab = !isClientView || (clientDetailsEnabled.entities && !row.hideallentities);
  const showDeliverablesTab = !isClientView || (clientDetailsEnabled.deliverables && !row.hidealldeliverables);

  if (isClientView && !showEntitiesTab && !showDeliverablesTab) {
    return (
      <div className="detail-container">
        <div className="no-details-message">No details available for this project.</div>
      </div>
    );
  }

  const selectedTab: "entities" | "deliverables" =
    currentTab === "deliverables" && showDeliverablesTab
      ? "deliverables"
      : currentTab === "entities" && showEntitiesTab
      ? "entities"
      : showDeliverablesTab
      ? "deliverables"
      : "entities";

  return (
    <div className="detail-container">
      <Tabs value={selectedTab} onChange={(_, v) => onTabChange(projectId, v)}>
        {showEntitiesTab && (
          <Tab
            value="entities"
            label="Entities"
            sx={{ textTransform: "none", fontWeight: (theme) => theme.typography.fontWeightBold }}
          />
        )}
        {showDeliverablesTab && (
          <Tab
            value="deliverables"
            label="Deliverables"
            sx={{ textTransform: "none", fontWeight: (theme) => theme.typography.fontWeightBold }}
          />
        )}
      </Tabs>
      {/* Both nested grids stay mounted (toggled via CSS) once shown, so switching between
          Entities/Deliverables never remounts a DataGrid or touches any other project's row. */}
      {showEntitiesTab && (
        <div style={{ display: selectedTab === "entities" ? undefined : "none" }}>
          <EntitiesDetailGrid
            isClientView={isClientView}
            hideAllEntitiesState={hideAllEntitiesState}
            loadingEntities={loadingEntities}
            displayEnts={displayEnts}
            projectId={projectId}
            handleBulkEntityVisibilityToggle={handleBulkEntityVisibilityToggle}
            subViewCellRender={entityCellRender}
            ensureEntitiesLoaded={ensureEntitiesLoaded}
            entityLoadingMore={!!entityLoadingMoreByProjectRef.current[projectId]}
            canLoadMoreEntities={canLoadMoreEntities}
            onGridInstanceReady={registerEntityGridInstance}
          />
        </div>
      )}
      {showDeliverablesTab && (
        <div style={{ display: selectedTab === "deliverables" ? undefined : "none" }}>
          <DeliverablesDetailGrid
            isClientView={isClientView}
            hideAllDeliverablesState={hideAllDeliverablesState}
            loadingDeliverables={loadingDeliverables}
            displayDels={displayDels}
            projectId={projectId}
            handleBulkDeliverableVisibilityToggle={handleBulkDeliverableVisibilityToggle}
            subViewCellRender={deliverableCellRender}
            ensureDeliverablesLoaded={ensureDeliverablesLoaded}
            deliverableLoadingMore={!!deliverableLoadingMoreByProjectRef.current[projectId]}
            canLoadMoreDeliverables={canLoadMoreDeliverables}
            onGridInstanceReady={registerDeliverableGridInstance}
          />
        </div>
      )}
    </div>
  );
};

const ProjectsView: React.FunctionComponent = () => {
  const { user } = useContext(OidcIdentityContext);
  const isInIframe = useMemo(() => window.self !== window.top, []);
  const gridRef = useRef<any>(null);

  // Per-project force-render functions for currently-mounted ProjectDetailPanel instances.
  // Notifying via this registry re-renders just that panel through normal React state,
  // instead of calling DevExtreme's repaintRows on the master-detail row (which destroys
  // and recreates the row's DOM, causing a visible glitch for every expanded project).
  const projectDetailUpdatersRef = useRef<Record<string, () => void>>({});
  const registerProjectDetailUpdater = useCallback((projectId: string, fn: (() => void) | null) => {
    if (fn) projectDetailUpdatersRef.current[projectId] = fn;
    else delete projectDetailUpdatersRef.current[projectId];
  }, []);
  const notifyProjectDetailChanged = useCallback((projectId: string) => {
    projectDetailUpdatersRef.current[projectId]?.();
  }, []);
  const notifyAllProjectDetailsChanged = useCallback(() => {
    Object.values(projectDetailUpdatersRef.current).forEach((fn) => fn?.());
  }, []);
  const [projectsGridState, setProjectsGridState] = useRecoilState(projectsGridStateAtom);
  // Read inside customLoad without being a dependency of the memoized grid below -
  // otherwise every StateStoring save (e.g. a column chooser toggle) would recompute
  // the whole <DataGrid> element and visibly flicker/reset the grid.
  const projectsGridStateRef = useRef(projectsGridState);
  projectsGridStateRef.current = projectsGridState;
  const [tasksSelectionCount, setTasksSelectionCount] = useState<number>(0);
  const [tasksToolbarControls, setTasksToolbarControls] = useState<any>(null);
  const handleTasksSaveClick = useCallback(async () => {
    if (!tasksToolbarControls?.save) return;
    try {
      // show only the toolbar button disabled/label state
      setTasksIsSaving(true);
      await tasksToolbarControls.save();
      // clear dirty flag when save completes
      setTasksIsDirty(false);
    } catch (err) {
      console.error('Tasks save failed', err);
    } finally {
      setTasksIsSaving(false);
    }
  }, [tasksToolbarControls]);
  const [tasksSearch, setTasksSearch] = useState<string>("");
  
  const [projectsGridRowsViewEl, setProjectsGridRowsViewEl] = useState<HTMLElement | null>(null);
  const [isClientUser, setIsClientUser] = useState<boolean>(false);
  const [hasDeterminedUserType, setHasDeterminedUserType] = useState<boolean>(false);
  const [clientDetailsEnabled, setClientDetailsEnabled] = useState<{
    entities: boolean;
    deliverables: boolean;
    tasks: boolean;
  }>({
    entities: false,
    deliverables: false,
    tasks: false,
  });
  const [clientStatusDetailsEnabled, setClientStatusDetailsEnabled] = useState<boolean>(true);
  const [effectiveShowProjects, setEffectiveShowProjects] =
    useState<boolean | undefined>(false);
  const [draftShowProjects, setDraftShowProjects] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const pageSize = PROJECTS_PAGE_SIZE;
  const [rows, setRows] = useState<ProjectRow[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [hiddenProjectIdsForCount, setHiddenProjectIdsForCount] = useState<number[]>([]);
  const rowsRef = useRef<ProjectRow[]>([]);
  const totalCountRef = useRef<number>(0);
  const isDirtyRef = useRef<boolean>(false);
  // Per-row visibility overrides read by `visibilityRender`, so toggling doesn't need
  // to push a full dataSource reload into DevExtreme (avoids column-width recalculation glitch).
  const projectVisibilityRef = useRef<Record<string, boolean>>({});
  // Same idea for nested entities/deliverables grids: projectId -> itemId -> visible.
  const entityVisibilityMapRef = useRef<Record<string, Record<string, boolean>>>({});
  const deliverableVisibilityMapRef = useRef<Record<string, Record<string, boolean>>>({});
  // Registry of the live DevExtreme instances for each expanded project's nested grids,
  // so a single entity/deliverable toggle can repaint just that row in just that grid.
  const entityGridInstancesRef = useRef<Record<string, any>>({});
  const deliverableGridInstancesRef = useRef<Record<string, any>>({});
  const blocker = useBlocker(isDirtyRef.current);
  // Rebuilds rowsRef + the visibility-override refs from a given rows array. Used by the
  // [rows] sync effect below, and called synchronously (before any repaint) by the
  // discard handlers so reverted values aren't lost to that effect's async timing.
  const syncVisibilityRefsFromRows = useCallback((rowsToSync: ProjectRow[]) => {
    rowsRef.current = rowsToSync;
    rowsToSync.forEach((r) => {
      projectVisibilityRef.current[r.id] = !!r.visibility;
      if (r.entities) {
        const map: Record<string, boolean> = {};
        r.entities.forEach((e) => { map[String(e.id)] = !!e.visibility; });
        entityVisibilityMapRef.current[r.id] = map;
      }
      if (r.deliverables) {
        const map: Record<string, boolean> = {};
        r.deliverables.forEach((d) => { map[String(d.id)] = !!d.visibility; });
        deliverableVisibilityMapRef.current[r.id] = map;
      }
    });
  }, []);
  useEffect(() => {
    syncVisibilityRefsFromRows(rows);
  }, [rows, syncVisibilityRefsFromRows]);
  useEffect(() => {
    totalCountRef.current = totalCount;
  }, [totalCount]);
  const [lastProjectsFetchCount, setLastProjectsFetchCount] = useState<number>(0);
  const [pending, setPending] = useState<boolean>(false);
  // True from clicking Client/Internal view until the reloaded projects arrive.
  const [viewSwitching, setViewSwitching] = useState<boolean>(false);
  const [detailTabByProject, setDetailTabByProject] = useState<
    Record<string, "entities" | "deliverables">
  >({});
  const [entityLoadingByProject, setEntityLoadingByProject] = useState<
    Record<string, boolean>
  >({});
  const [deliverableLoadingByProject, setDeliverableLoadingByProject] =
    useState<Record<string, boolean>>({});
  const [entityTotalByProject, setEntityTotalByProject] = useState<
    Record<string, number>
  >({});
  const [deliverableTotalByProject, setDeliverableTotalByProject] = useState<
    Record<string, number>
  >({});
  const [entityLastFetchCountByProject, setEntityLastFetchCountByProject] =
    useState<Record<string, number>>({});
  const [deliverableLastFetchCountByProject, setDeliverableLastFetchCountByProject] =
    useState<Record<string, number>>({});
  const [entityLoadingMoreByProject, setEntityLoadingMoreByProject] = useState<
    Record<string, boolean>
  >({});
  const [deliverableLoadingMoreByProject, setDeliverableLoadingMoreByProject] = useState<
    Record<string, boolean>
  >({});
  // Ref mirrors of the per-project maps above. renderDetail and the per-project
  // load/toggle handlers read from these refs instead of depending on the maps
  // directly, so an update scoped to one project doesn't change every handler's
  // identity and force DevExtreme to redraw every currently-expanded detail row.
  const detailTabByProjectRef = useRef(detailTabByProject);
  detailTabByProjectRef.current = detailTabByProject;
  const entityLoadingByProjectRef = useRef(entityLoadingByProject);
  entityLoadingByProjectRef.current = entityLoadingByProject;
  const deliverableLoadingByProjectRef = useRef(deliverableLoadingByProject);
  deliverableLoadingByProjectRef.current = deliverableLoadingByProject;
  const entityTotalByProjectRef = useRef(entityTotalByProject);
  entityTotalByProjectRef.current = entityTotalByProject;
  const deliverableTotalByProjectRef = useRef(deliverableTotalByProject);
  deliverableTotalByProjectRef.current = deliverableTotalByProject;
  const entityLastFetchCountByProjectRef = useRef(entityLastFetchCountByProject);
  entityLastFetchCountByProjectRef.current = entityLastFetchCountByProject;
  const deliverableLastFetchCountByProjectRef = useRef(deliverableLastFetchCountByProject);
  deliverableLastFetchCountByProjectRef.current = deliverableLastFetchCountByProject;
  const entityLoadingMoreByProjectRef = useRef(entityLoadingMoreByProject);
  entityLoadingMoreByProjectRef.current = entityLoadingMoreByProject;
  const deliverableLoadingMoreByProjectRef = useRef(deliverableLoadingMoreByProject);
  deliverableLoadingMoreByProjectRef.current = deliverableLoadingMoreByProject;
  // Tracks hidden entity/deliverable IDs from pages the user has NOT yet loaded into the grid.
  const [entityOrphanHiddenIdsByProject, setEntityOrphanHiddenIdsByProject] = useState<
    Record<string, number[]>
  >({});
  const [deliverableOrphanHiddenIdsByProject, setDeliverableOrphanHiddenIdsByProject] = useState<
    Record<string, number[]>
  >({});
  const projectsLoadSeqRef = useRef(0);
  const viewChangeReloadPendingRef = useRef(false);
  const entityAbortControllersRef = useRef<
    Record<string, AbortController | undefined>
  >({});
  const deliverableAbortControllersRef = useRef<
    Record<string, AbortController | undefined>
  >({});
  const [actionsMenuPosition, setActionsMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [actionRow, setActionRow] = useState<ProjectRow | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const originalHiddenColumnsRef = useRef<string[]>([]);
  const [showTempBanner, setShowTempBanner] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  // sync ref so handleGridContentReady can read latest value without being in deps
  useEffect(() => { hiddenColumnsRef.current = hiddenColumns; }, [hiddenColumns]);
  const suppressNextEffectRef = useRef(false);
  const skipNextProjectsLoadRef = useRef(false);
  const [loadingVisibility, setLoadingVisibility] = useState<boolean>(true);
  const [savingVisibility, setSavingVisibility] = useState<boolean>(false);
  const [visibilityId, setVisibilityId] = useState<string | null>(null);
  const [isMessagesOpen, setIsMessagesOpen] = useState(false);
  const [messagesProject, setMessagesProject] = useState<ProjectRow | null>(null);
  const [siteUsers, setSiteUsers] = useState<MentionableUser[]>([]);
  const [isStatusDetailsOpen, setIsStatusDetailsOpen] = useState(false);
  const [statusDetailsProject, setStatusDetailsProject] = useState<ProjectRow | null>(null);
  const [settingsTab, setSettingsTab] = useState<"project" | "status">("project");
  const [projectSettingsData, setProjectSettingsData] = useState<Array<{key: string; label: string; note: string; visibility: boolean}>>([]);
  const [loadingProjectSettings, setLoadingProjectSettings] = useState(false);
  const [savingProjectSettings, setSavingProjectSettings] = useState(false);
  const [projectSettingsId, setProjectSettingsId] = useState<string | null>(null);
  const originalProjectSettingsRef = useRef<Array<{key: string; label: string; note: string; visibility: boolean}>>([]);
  const originalStatusManagementRef = useRef<ProjectsCustomizationStatusDto[]>([]);
  const [isClientView, setIsClientView] = useState<boolean>(false);
  // Ref mirrors read by ProjectDetailPanel instances (mounted once per row-expand) so
  // isClientView/clientDetailsEnabled changes don't need to be passed as fresh React
  // props - they're picked up on the panel's own next force-rendered pass instead.
  const isClientViewRef = useRef(isClientView);
  isClientViewRef.current = isClientView;
  const clientDetailsEnabledRef = useRef(clientDetailsEnabled);
  clientDetailsEnabledRef.current = clientDetailsEnabled;
  useEffect(() => {
    notifyAllProjectDetailsChanged();
  }, [isClientView, clientDetailsEnabled.entities, clientDetailsEnabled.deliverables, notifyAllProjectDetailsChanged]);
  const originalRowsRef = useRef<ProjectRow[]>([]);
  const [dirtyBaselineVersion, setDirtyBaselineVersion] = useState(0);
  const entitiesDirtyByProjectRef = useRef<Set<string>>(new Set());
  const deliverablesDirtyByProjectRef = useRef<Set<string>>(new Set());
  const [savingChanges, setSavingChanges] = useState<boolean>(false);
  const [statusManagementData, setStatusManagementData] = useState<ProjectsCustomizationStatusDto[]>([]);
  const [loadingStatusData, setLoadingStatusData] = useState(false);
  const [savingStatusData, setSavingStatusData] = useState(false);
  const [resettingProjectSettings, setResettingProjectSettings] = useState(false);
  const [confirmExitSettingsOpen, setConfirmExitSettingsOpen] = useState(false);
  const [confirmResetProjectSettingsOpen, setConfirmResetProjectSettingsOpen] = useState(false);
  const [confirmCancelGridOpen, setConfirmCancelGridOpen] = useState(false);
  const [confirmCancelTasksOpen, setConfirmCancelTasksOpen] = useState(false);
  const [confirmSwitchClientViewOpen, setConfirmSwitchClientViewOpen] = useState(false);
  const [confirmLeavePageOpen, setConfirmLeavePageOpen] = useState(false);
  const suppressColumnOptionChangedRef = useRef(false);
  const hasInitializedColumnVisibilityRef = useRef(false);
  // null sentinel ensures first call for a new instance always applies, even when hidden=[]
  const lastAppliedHiddenColumnsKeyRef = useRef<string | null>(null);
  const lastAppliedGridInstanceRef = useRef<any>(null);
  const initialColumnsOrderRef = useRef<string[] | null>(null);
  const hiddenColumnsRef = useRef<string[]>([]);
  const [isClientViewTransitionLoading, setIsClientViewTransitionLoading] = useState(false);
  const [clientHiddenColumns, setClientHiddenColumns] = useState<string[]>(
    DEFAULT_CLIENT_HIDDEN_COLUMNS,
  );
  const clientHiddenColumnsRef = useRef(clientHiddenColumns);
  clientHiddenColumnsRef.current = clientHiddenColumns;
  const [isProjectDetailsOpen, setIsProjectDetailsOpen] = useState(false);
  const [projectDetailsProject, setProjectDetailsProject] = useState<ProjectRow | null>(null);
  const [isEngagementManagersOpen, setIsEngagementManagersOpen] = useState(false);
  const [engagementManagersForDialog, setEngagementManagersForDialog] = useState<ProjectPerson[]>([]);
  const [isEngagementHoverOpen, setIsEngagementHoverOpen] = useState(false);
  const [engagementHoverAnchorEl, setEngagementHoverAnchorEl] = useState<HTMLElement | VirtualElement | null>(null);
  const [engagementHoverManagers, setEngagementHoverManagers] = useState<ProjectPerson[]>([]);
  const engagementHoverCloseTimerRef = useRef<number | null>(null);
  const engagementHoverCardRef = useRef<HTMLDivElement | null>(null);
  const [hasBootstrapped, setHasBootstrapped] = useState(false);
  const { showSnackbar } = useSnackbar();
  const [activeView, setActiveView] = useState<'project'|'task'>('project');
  const [_tasksCount, setTasksCount] = useState<number>(0);
  const [tasksIsDirty, setTasksIsDirty] = useState<boolean>(false);
  const [tasksIsSaving, setTasksIsSaving] = useState<boolean>(false);
  const tasksCancelRef = useRef<(() => void) | null>(null);
  // Set when a tab switch (Projects <-> Tasks) is blocked by unsaved Tasks changes;
  // consumed by the existing leave-page confirm dialog's handlers.
  const pendingViewSwitchRef = useRef<'project' | 'task' | null>(null);

  const uiProjectSettingsCacheRef = useRef<
    { siteId: number; parsed: any | null } | null
  >(null);
  const uiProjectSettingsLoadPromiseRef = useRef<Promise<any | null> | null>(
    null,
  );

  const siteUsersCacheRef = useRef<
    { siteId: number; users: MentionableUser[] } | null
  >(null);
  const siteUsersLoadPromiseRef = useRef<
    Promise<MentionableUser[]> | null
  >(null);

  const normalizeUserId = (value?: string) => (value || "").trim().toLowerCase();

  const getCurrentUserEmail = useCallback(() => {
    const profile = (user as any)?.profile || {};
    const preferred = profile.preferred_username;
    const email = profile.email;
    const name = profile.name;
    return normalizeUserId(preferred || email || name || "unknown-user");
  }, [user]);

  const isHiddenEngagementManagerForProject = (row?: ProjectRow | null) => {
    if (!row) return false;
    const currentUserEmail = getCurrentUserEmail();
    return (row.engagementManagers || []).some(
      (manager) =>
        normalizeUserId(manager?.email) === currentUserEmail &&
        manager?.visibility === false,
    );
  };


  const getNotificationReads = (): ProjectNotificationRead[] => {
    try {
      const raw = sessionStorage.getItem(TIM_PROJECT_NOTIFICATION_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const setNotificationReads = (reads: ProjectNotificationRead[]) => {
    try {
      sessionStorage.setItem(
        TIM_PROJECT_NOTIFICATION_STORAGE_KEY,
        JSON.stringify(reads),
      );
    } catch {}
  };

  const getReadEntry = useCallback((
    siteId: number,
    projectIdMdm: number,
  ): ProjectNotificationRead | undefined => {
    const currentUserEmail = getCurrentUserEmail();
    return getNotificationReads().find(
      (entry) =>
        entry.HQSiteId === siteId &&
        entry.ProjectIdMDM === projectIdMdm &&
        (normalizeUserId(entry.LastMessageReadUser) === currentUserEmail ||
          normalizeUserId(entry.LastStatusNoteReadUser) === currentUserEmail),
    );
  }, [getCurrentUserEmail]);

  const upsertReadEntry = (
    siteId: number,
    projectIdMdm: number,
    updates: Partial<
      Pick<ProjectNotificationRead, "LastMessageReadTime" | "LastStatusNoteReadTime">
    >,
  ) => {
    const currentUserEmail = getCurrentUserEmail();
    const reads = getNotificationReads();
    const index = reads.findIndex(
      (entry) =>
        entry.HQSiteId === siteId &&
        entry.ProjectIdMDM === projectIdMdm &&
        (normalizeUserId(entry.LastMessageReadUser) === currentUserEmail ||
          normalizeUserId(entry.LastStatusNoteReadUser) === currentUserEmail),
    );

    const existing =
      index >= 0
        ? reads[index]
        : {
            HQSiteId: siteId,
            ProjectIdMDM: projectIdMdm,
            LastMessageReadUser: currentUserEmail,
            LastMessageReadTime: "",
            LastStatusNoteReadUser: currentUserEmail,
            LastStatusNoteReadTime: "",
          };

    const updated: ProjectNotificationRead = {
      ...existing,
      LastMessageReadUser: currentUserEmail,
      LastStatusNoteReadUser: currentUserEmail,
      LastMessageReadTime:
        updates.LastMessageReadTime ?? existing.LastMessageReadTime,
      LastStatusNoteReadTime:
        updates.LastStatusNoteReadTime ?? existing.LastStatusNoteReadTime,
    };

    if (index >= 0) reads[index] = updated;
    else reads.push(updated);

    setNotificationReads(reads);
  };

  // API timestamps sometimes arrive without timezone (e.g. "2026-05-07T15:31:31.049").
  // JS `Date` parses those as *local time*, but our sessionStorage read-times are
  // stored as UTC ("...Z"). That mismatch can suppress the unread dot depending on
  // the user's timezone. Treat timezone-less ISO strings as UTC for comparisons.
  const parseApiTimestamp = useCallback((value?: string): Date | null => {
    if (!value) return null;
    const raw = String(value).trim();
    if (!raw) return null;

    const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);
    const looksIso = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw);
    const normalized = !hasTz && looksIso ? `${raw}Z` : raw;

    const d = new Date(normalized);
    return Number.isFinite(d.getTime()) ? d : null;
  }, []);

  const toIsoUtc = useCallback((value?: string): string | undefined => {
    const d = parseApiTimestamp(value);
    return d ? d.toISOString() : undefined;
  }, [parseApiTimestamp]);

  const isUnreadSinceLastRead = useCallback((latestTime?: string, readTime?: string) => {
    if (!latestTime) return false;
    if (!readTime) return true;
    const latest = parseApiTimestamp(latestTime);
    const read = parseApiTimestamp(readTime);
    if (!latest) return false;
    if (!read) return true;
    return latest.getTime() > read.getTime();
  }, [parseApiTimestamp]);


  useEffect(() => {
    return () => {
      if (engagementHoverCloseTimerRef.current !== null) {
        window.clearTimeout(engagementHoverCloseTimerRef.current);
      }
    };
  }, []);

//Hide the right scrollbar from parent componant
  useEffect(() => {
    const hideScrollbars = (el: HTMLElement | null) => {
      if (!el) return;
      el.style.setProperty("-ms-overflow-style", "none", "important");
      el.style.setProperty("scrollbar-width", "none", "important");
    };

    hideScrollbars(document.getElementById("cui-layout_main"));
    hideScrollbars(document.getElementById("root"));

    const style = document.createElement("style");
    style.textContent = `
      #cui-layout_main::-webkit-scrollbar,
      #root::-webkit-scrollbar,
      html::-webkit-scrollbar,
      body::-webkit-scrollbar {
        display: none !important;
        width: 0 !important;
        height: 0 !important;
      }
    `;
    style.className = "tp-scrollbar-hide";
    document.head.appendChild(style);

    return () => {
      document.querySelectorAll("style.tp-scrollbar-hide").forEach(el => el.remove());
      document.getElementById("cui-layout_main")?.style.removeProperty("-ms-overflow-style");
      document.getElementById("cui-layout_main")?.style.removeProperty("scrollbar-width");
      document.getElementById("root")?.style.removeProperty("-ms-overflow-style");
      document.getElementById("root")?.style.removeProperty("scrollbar-width");
    };
  }, []);

  const createVirtualAnchor = useCallback((el: HTMLElement): VirtualElement => {
    // DevExtreme virtualizes/recycles row DOM nodes. If we keep reading
    // `getBoundingClientRect()` from the element after virtualization, the
    // popper can jump to a different cell ("random" location). Snapshot once.
    const rect = el.getBoundingClientRect();
    return {
      getBoundingClientRect: () => rect,
      contextElement: el,
    };
  }, []);

   useEffect(() => {
    if (!isEngagementHoverOpen) return;
    
    const handleClose = () => setIsEngagementHoverOpen(false);
    
    // Close on scroll/resize to avoid stale anchoring during virtualization.
    // But do NOT close if the scroll happened inside the engagement hover card.
    const handleScroll = (e: Event) => {
      const target = e.target as Element;
      const isInsideCard = engagementHoverCardRef.current?.contains(target);
      if (!isInsideCard) {
        handleClose();
      }
    };
    
    const handleWheel = (e: WheelEvent) => {
      const target = e.target as Element;
      const isInsideCard = engagementHoverCardRef.current?.contains(target);
      if (!isInsideCard) {
        handleClose();
      }
    };
    
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleClose);
    window.addEventListener("wheel", handleWheel, { passive: true } as any);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleClose);
      window.removeEventListener("wheel", handleWheel as any);
    };
  }, [isEngagementHoverOpen]);

  const openEngagementHover = useCallback((
    anchorEl: HTMLElement,
    managers: ProjectPerson[],
  ) => {
    if (engagementHoverCloseTimerRef.current !== null) {
      window.clearTimeout(engagementHoverCloseTimerRef.current);
      engagementHoverCloseTimerRef.current = null;
    }
    setEngagementHoverAnchorEl(createVirtualAnchor(anchorEl));
    setEngagementHoverManagers(managers);
    setIsEngagementHoverOpen(true);
  }, [createVirtualAnchor]);

  const scheduleCloseEngagementHover = useCallback(() => {
    if (engagementHoverCloseTimerRef.current !== null) {
      window.clearTimeout(engagementHoverCloseTimerRef.current);
    }
    engagementHoverCloseTimerRef.current = window.setTimeout(() => {
      setIsEngagementHoverOpen(false);
    }, 120);
  }, []);

  const keepEngagementHoverOpen = useCallback(() => {
    if (engagementHoverCloseTimerRef.current !== null) {
      window.clearTimeout(engagementHoverCloseTimerRef.current);
      engagementHoverCloseTimerRef.current = null;
    }
  }, []);

  const handleOpenEngagementManagersFromHover = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    event.stopPropagation();
    setEngagementManagersForDialog(engagementHoverManagers);
    setIsEngagementManagersOpen(true);
    setIsEngagementHoverOpen(false);
  };

  const copyEmailToClipboard = async (email?: string) => {
    if (!email) return;

    let copied = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(email);
        copied = true;
      }
    } catch {
      // Continue to fallback
    }

    // Fallback to legacy execCommand
    if (!copied) {
      const previousElement = document.activeElement as HTMLElement | null;
      const scrollPos = { x: window.scrollX, y: window.scrollY };

      const input = document.createElement("input");
      input.type = "text";
      input.value = email;
      input.style.position = "fixed";
      input.style.opacity = "0";
      document.body.appendChild(input);

      try {
        input.focus({ preventScroll: true });
        input.select();
        copied = document.execCommand("copy");
      } catch {
        // Continue
      } finally {
        document.body.removeChild(input);
        previousElement?.focus({ preventScroll: true });
        window.scrollTo(scrollPos.x, scrollPos.y);
      }
    }  
  };

  const applyGridColumnVisibility = useCallback((hidden: string[]) => {
    const inst = gridRef.current?.instance;
    if (!inst) return;
    if (!initialColumnsOrderRef.current || lastAppliedGridInstanceRef.current !== inst) {
      try {
        const cols: any[] = (typeof inst.getColumns === 'function') ? inst.getColumns() : [];
        initialColumnsOrderRef.current = cols.map((c: any) => c.name || c.dataField || String(c.caption || '')).filter(Boolean);
      } catch {}
    }

    // DevExtreme can recreate the grid instance; re-apply visibility for a new instance
    // even if the hidden-columns set is unchanged.
    if (lastAppliedGridInstanceRef.current !== inst) {
      lastAppliedGridInstanceRef.current = inst;
      lastAppliedHiddenColumnsKeyRef.current = null;
    }

    // No early-return on an unchanged hidden set: DevExtreme can re-show columns during load
    // (state restore / prop re-apply), so always reconcile; the loop below only touches mismatches.
    // Client-view hiding lives here (not as JSX `visible` props) so devextreme-react's
    // controlled-prop rollback can't fight the chooser.
    // Column-chooser (`hidden`) choices are internal-only; client view is driven purely by project settings.
    const effectiveHidden = isClientViewRef.current
      ? Array.from(new Set([...clientHiddenColumnsRef.current, ...CLIENT_VIEW_ALWAYS_HIDDEN_COLUMNS]))
      : (hidden || []);
    const hiddenKey = [...effectiveHidden].sort().join("|");
    const normalize = (s: any) => String(s || '').trim().toLowerCase();
    const hiddenSet = new Set(effectiveHidden.map(normalize));
    suppressColumnOptionChangedRef.current = true;
    try {
      inst.beginUpdate?.();
      const cols: any[] = (typeof inst.getColumns === 'function') ? inst.getColumns() : (inst.option && inst.option('columns')) || [];
      const colInfos = cols.map((c: any) => ({ col: c, key: c?.name || c?.dataField || String(c?.caption || ''), norm: normalize(c?.name || c?.dataField || String(c?.caption || '')) })).filter((x: any) => x.key);

      // Columns the user can't hide keep whatever visibility the grid gives them.
      const isLocked = (ci: any) => ci.col?.allowHiding === false;
      const shouldShow = (ci: any) => (isLocked(ci) ? inst.columnOption(ci.key, 'visible') !== false : !hiddenSet.has(ci.norm));
      // Reordering is disabled, so declaration order is the canonical column order.
      const assignments: Record<string, number> = {};
      let nextIndex = 0;
      for (const ci of colInfos) {
        if (shouldShow(ci)) assignments[ci.key] = nextIndex++;
      }

      // Address columns by name: getColumns() can include command columns (expand), so its
      // positions don't line up with columnOption's numeric indexes.
      for (const ci of colInfos) {
        const shouldBeVisible = shouldShow(ci);
        try {
          if (!isLocked(ci) && inst.columnOption(ci.key, 'visible') !== shouldBeVisible) inst.columnOption(ci.key, 'visible', shouldBeVisible);
        } catch {}
        if (shouldBeVisible) {
          const idx = assignments[ci.key];
          try { if (typeof idx === 'number' && inst.columnOption(ci.key, 'visibleIndex') !== idx) inst.columnOption(ci.key, 'visibleIndex', idx); } catch {}
        }
      }

      lastAppliedHiddenColumnsKeyRef.current = hiddenKey;
      lastAppliedGridInstanceRef.current = inst;
    } finally {
      inst.endUpdate?.();
    }
    // Mark that we've applied initial column visibility at least once. Used to
    // ignore DevExtreme's startup optionChanged noise.
    if (!hasInitializedColumnVisibilityRef.current) {
      hasInitializedColumnVisibilityRef.current = true;
    }
    window.setTimeout(() => {
      suppressColumnOptionChangedRef.current = false;
    }, 0);
  }, []);

  const getUiForProjectSettings = useCallback(async (): Promise<any | null> => {
    const siteId = parseInt(sessionStorage.getItem("siteId") || "0");
    if (!siteId || Number.isNaN(siteId)) return null;

    if (uiProjectSettingsCacheRef.current?.siteId === siteId) {
      return uiProjectSettingsCacheRef.current.parsed;
    }

    if (uiProjectSettingsLoadPromiseRef.current) {
      return uiProjectSettingsLoadPromiseRef.current;
    }

    uiProjectSettingsLoadPromiseRef.current = (async () => {
      try {
        const resp = await createSitesClientApi.tim_GetProjectSettings(siteId);
        const data: any =
          (resp as any)?.data?.data ?? (resp as any)?.data ?? resp;
        const record = Array.isArray(data)
          ? data.find((x: any) => x?.property === "UIForProjectSettings") ??
            data[0]
          : data;

        const rawValue = record?.value;
        const parsed = rawValue ? JSON.parse(rawValue) : null;
        uiProjectSettingsCacheRef.current = { siteId, parsed };
        return parsed;
      } catch (error) {
        console.error("Failed to fetch UIForProjectSettings:", error);
        uiProjectSettingsCacheRef.current = { siteId, parsed: null };
        return null;
      } finally {
        uiProjectSettingsLoadPromiseRef.current = null;
      }
    })();

    return uiProjectSettingsLoadPromiseRef.current;
  }, []);

  const getSiteUsersForMentions = useCallback(async (): Promise<MentionableUser[]> => {
    const siteId = parseInt(sessionStorage.getItem("siteId") || "0");
    if (!siteId || Number.isNaN(siteId)) return [];

    if (siteUsersCacheRef.current?.siteId === siteId) {
      return siteUsersCacheRef.current.users;
    }

    if (siteUsersLoadPromiseRef.current) {
      return siteUsersLoadPromiseRef.current;
    }

    siteUsersLoadPromiseRef.current = (async () => {
      try {
        const response: any = await createSitesClientApi.tim_GetSiteUsers(siteId);
        const users = response?.data?.data ?? response?.data ?? response ?? [];
        const normalized = Array.isArray(users) ? (users as MentionableUser[]) : [];
        siteUsersCacheRef.current = { siteId, users: normalized };
        return normalized;
      } catch (error) {
        console.error("Failed to load site users for mentions:", error);
        siteUsersCacheRef.current = { siteId, users: [] };
        return [];
      } finally {
        siteUsersLoadPromiseRef.current = null;
      }
    })();

    return siteUsersLoadPromiseRef.current;
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const users = await getSiteUsersForMentions();
      if (!cancelled) setSiteUsers(users);
    })();

    return () => {
      cancelled = true;
    };
  }, [getSiteUsersForMentions]);

  const fetchClientHiddenColumns = useCallback(async (): Promise<string[]> => {
    const hiddenSet = new Set<string>(DEFAULT_CLIENT_HIDDEN_COLUMNS);

    // Prefer current in-memory settings (if available) so client view reflects
    // latest toggles immediately after saving settings.
    if (projectSettingsData.length > 0) {
      projectSettingsData.forEach((setting) => {
        const columnName = CLIENT_SETTING_TO_COLUMN_NAME_MAP[setting.key];
        if (!columnName) return;
        if (setting.visibility === true) hiddenSet.delete(columnName);
        else hiddenSet.add(columnName);
      });
      return Array.from(hiddenSet);
    }

    try {
      const parsed = await getUiForProjectSettings();
      if (!parsed) return Array.from(hiddenSet);

      Object.keys(CLIENT_SETTING_TO_COLUMN_NAME_MAP).forEach((key) => {
        const columnName = CLIENT_SETTING_TO_COLUMN_NAME_MAP[key];
        const settingValue = parsed?.[key];
        if (settingValue === true) hiddenSet.delete(columnName);
        else hiddenSet.add(columnName);
      });
    } catch (error) {
      console.error("Failed to fetch client view project settings:", error);
    }

    return Array.from(hiddenSet);
  }, [projectSettingsData, getUiForProjectSettings]);

  const toBoolean = (value: any, defaultValue: boolean) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const v = value.trim().toLowerCase();
      if (v === "true") return true;
      if (v === "false") return false;
    }
    return defaultValue;
  };

  // In Client View (including true client users), use UIForProjectSettings to
  // control whether Entities/Deliverables details are available.
  useEffect(() => {
    if (!isClientView) {
      setClientDetailsEnabled({ entities: false, deliverables: false, tasks: false });
      setClientStatusDetailsEnabled(true);
      return;
    }

    // If the settings dialog has loaded/edited data in-memory, prefer it.
    // This ensures the chevron updates correctly after saving settings and
    // also reflects changes immediately while editing in ClientView.
    const getSetting = (key: string) => projectSettingsData.find((p) => p.key === key)?.visibility;
    const entitiesFromState = getSetting("Entities");
    const deliverablesFromState = getSetting("Deliverables");
    const statusDetailsFromState = getSetting("StatusDetails");
    const tasksFromState = getSetting("Task");
    if (typeof entitiesFromState === "boolean" || typeof deliverablesFromState === "boolean" || typeof statusDetailsFromState === "boolean" || typeof tasksFromState === "boolean") {
      setClientDetailsEnabled({
        entities: typeof entitiesFromState === "boolean" ? entitiesFromState : false,
        deliverables: typeof deliverablesFromState === "boolean" ? deliverablesFromState : false,
        tasks: typeof tasksFromState === "boolean" ? tasksFromState : false,
      });
      setClientStatusDetailsEnabled(typeof statusDetailsFromState === "boolean" ? statusDetailsFromState : false);
      return;
    }

    // Use cached settings immediately (prevents chevron appearing late).
    const cached = uiProjectSettingsCacheRef.current?.parsed;
    if (cached) {
      setClientDetailsEnabled({
        entities: toBoolean(cached?.Entities, false),
        deliverables: toBoolean(cached?.Deliverables, false),
        tasks: toBoolean(cached?.Task, false),
      });
      setClientStatusDetailsEnabled(toBoolean(cached?.StatusDetails, false));
    } else {
      // Pessimistic only if we truly have nothing yet.
      setClientDetailsEnabled({ entities: false, deliverables: false, tasks: false });
      setClientStatusDetailsEnabled(false);
    }

    let cancelled = false;
    (async () => {
      try {
        const parsed = await getUiForProjectSettings();
        if (!parsed) return;
        const entitiesEnabled = toBoolean(parsed?.Entities, false);
        const deliverablesEnabled = toBoolean(parsed?.Deliverables, false);
        const statusDetailsEnabled = toBoolean(parsed?.StatusDetails, false);
        const tasksEnabled = toBoolean(parsed?.Task, false);

        if (!cancelled) {
          setClientDetailsEnabled({
            entities: entitiesEnabled,
            deliverables: deliverablesEnabled,
            tasks: tasksEnabled,
          });
          setClientStatusDetailsEnabled(statusDetailsEnabled);
        }
      } catch (error) {
        console.error("Failed to fetch client view detail settings:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isClientView, projectSettingsData, getUiForProjectSettings]);

  const loadData = React.useCallback(async (force: boolean = false) => {
    const seq = ++projectsLoadSeqRef.current;
    setPending(true);
    const requestedPage = page;
    try {
      const currentUserEmail = getCurrentUserEmail();

      // Always load projects for engagement view
      var siteId = parseInt(sessionStorage.getItem("siteId")!);
      // `force=true` is used for refreshes (e.g., after Status Management save).
      // In that case, re-fetch from the beginning up to the currently-loaded
      // page count so existing rows get updated (not just appended).
      const loadedPages = Math.max(1, requestedPage);
      const offset = force ? 0 : (requestedPage - 1) * pageSize;
      let limit = force ? loadedPages * pageSize : pageSize;
      if (force) {
        // IMPORTANT: When the user has already loaded additional pages,
        // refresh must cover *all currently loaded rows* (not just page*size)
        // so Status Management changes are reflected across the entire grid.
        const loadedCount = rowsRef.current?.length ?? 0;
        limit = Math.max(limit, loadedCount, pageSize);
        const knownTotal = totalCountRef.current;
        if (knownTotal > 0) {
          // Avoid requesting more than we know exists.
          limit = Math.min(limit, knownTotal);
        }
      }
      let projects: any[] = [];
      let total = 0;

      const resp: any = await createSitesClientApi.tim_Projects(siteId, offset, limit, isClientViewRef.current);

      // tim_Projects response shape has varied:
      // - Double-wrapped: { data: { data: { projects, totalcount } } }
      // - Single-wrapped: { data: { projects, totalcount } }
      // - Direct: { projects, totalcount }
      const payload =
        resp?.data?.data?.data ??
        resp?.data?.data ??
        resp?.data ??
        resp;

      const projectsCandidate =
        payload?.projects ??
        payload?.data?.projects ??
        payload?.data?.data?.projects ??
        [];

      projects = Array.isArray(projectsCandidate) ? projectsCandidate : [];

      const totalCandidate =
        payload?.totalcount ??
        payload?.totalCount ??
        payload?.data?.totalcount ??
        payload?.data?.totalCount ??
        payload?.data?.data?.totalcount ??
        payload?.data?.data?.totalCount;
  
      const hiddenProjectsCandidate =
        payload?.hiddenprojects ??
        payload?.hiddenProjects ??
        payload?.data?.hiddenprojects ??
        payload?.data?.hiddenProjects ??
        payload?.data?.data?.hiddenprojects ??
        payload?.data?.data?.hiddenProjects ??
        [];

      const hiddenProjectIds = Array.from(
        new Set(
          (Array.isArray(hiddenProjectsCandidate)
            ? hiddenProjectsCandidate
            : []
          )
            .map((id: any) => Number(id))
            .filter((id: number) => Number.isFinite(id)),
        ),
      );

      const totalCoerced =
        typeof totalCandidate === "number"
          ? totalCandidate
          : totalCandidate !== undefined && totalCandidate !== null
          ? Number(totalCandidate)
          : Number.NaN;

      total =
        typeof totalCoerced === "number" && Number.isFinite(totalCoerced)
          ? totalCoerced
          : projects.length;

      const coerceVisibility = (value: any, defaultValue: boolean) => {
        if (typeof value === "boolean") return value;
        if (typeof value === "string") {
          const v = value.trim().toLowerCase();
          if (v === "true") return true;
          if (v === "false") return false;
        }
        if (typeof value === "number") {
          if (value === 1) return true;
          if (value === 0) return false;
        }
        return defaultValue;
      };

      const mapped: ProjectRow[] = projects.map((p) => {
        const lastMessageCreated =
          (p as any).lastmessagecreated ?? (p as any).lastMessageCreated;
        const lastMessageCreatedBy =
          (p as any).lastmessagecreatedby ?? (p as any).lastMessageCreatedBy;
        const lastNoteCreated =
          (p as any).lastnotecreated ?? (p as any).lastNoteCreated;
        const lastNoteCreatedBy =
          (p as any).lastnotecreatedby ?? (p as any).lastNoteCreatedBy;

        const lastMessageByNormalized = normalizeUserId(lastMessageCreatedBy);
        const lastNoteByNormalized = normalizeUserId(lastNoteCreatedBy);

        const hasUnreadMessage =
          !!lastMessageCreated &&
          (!!lastMessageByNormalized ? lastMessageByNormalized !== currentUserEmail : true);
        const hasUnreadNote =
          !!lastNoteCreated &&
          (!!lastNoteByNormalized ? lastNoteByNormalized !== currentUserEmail : true);

        const coerceBool = (v: any) =>
          typeof v === "string" ? v.toLowerCase() === "true" : !!v;
        const hideAllEntities = coerceBool(
          (p as any).hideallentities ?? (p as any).hideAllEntities ?? (p as any).HideAllEntities,
        );
        const hideAllDeliverables = coerceBool(
          (p as any).hidealldeliverables ??
            (p as any).hideAllDeliverables ??
            (p as any).HideAllDeliverables,
        );

        return {
        id: String(p.id ?? `${p.projectidmdm ?? Math.random()}`),
        clientId: p.entityid ?? 0,
        client: p.entity ?? "",
        projectDefaultName: p.projectdefaultname ?? p.projectname ?? "",
        project: p.projectname ?? p.projectdefaultname ?? "",
        projectCustomName: p.projectcustomname ?? "",
        consent7216:
          p.consent7216 === "Yes" ||
          p.consent7216 === "No" ||
          p.consent7216 === "Mixed"
            ? (p.consent7216 as any)
            : p.consent7216
            ? "Mixed"
            : "No",
        taxYear: p.taxyear || null,
        projectIdMdm: p.projectidmdm ?? undefined,
        projectIdTim: p.id ?? undefined,
        // Engagement managers / project users come back in the projects payload.
        // Use either `projectUsers` or `projectpeople` depending on API shape.
        engagementManagers: ((p as any).projectUsers || (p as any).projectpeople || []).map((pp: any) => ({
          id: pp.id,
          name: pp.name,
          employeenumber: pp.employeenumber,
          email: pp.email,
          projectidmdm: pp.projectidmdm,
          // Some API variants omit `visibility` or use different naming.
          // Treat missing visibility as visible.
          visibility: coerceVisibility(
            (pp as any).visibility ?? (pp as any).isVisible ?? (pp as any).isvisible,
            true,
          ),
        })),
        status: (p.status as any) || "New",
        // Some API variants omit `visibility` for client logins (already filtered).
        // Treat missing visibility as visible.
        visibility: coerceVisibility(
          (p as any).visibility ?? (p as any).isVisible ?? (p as any).isvisible,
          true,
        ),
        haveUnreadMessage: hasUnreadMessage,
        haveUnreadNote: hasUnreadNote,
        lastMessageCreated: lastMessageCreated ? String(lastMessageCreated) : undefined,
        lastMessageCreatedBy: lastMessageCreatedBy ? String(lastMessageCreatedBy) : undefined,
        lastNoteCreated: lastNoteCreated ? String(lastNoteCreated) : undefined,
        lastNoteCreatedBy: lastNoteCreatedBy ? String(lastNoteCreatedBy) : undefined,
        entities: (((p as any).entities as any[]) || undefined)?.map(
          (e, idx) => ({
            id: e.id ?? idx,
            entityId: e.entityid ?? 0,
            entity: e.entity ?? "",
            legalEntityType: e.legalentitytype ?? "",
            consent7216: e.consent7216 ?? undefined,
            imanageUrl: e.imanageurl ?? undefined,
            visibility: hideAllEntities
              ? false
              : coerceVisibility(
                  (e as any).visibility ?? (e as any).isVisible ?? (e as any).isvisible,
                  true,
                ),
          }),
        ),
        hideallentities: hideAllEntities,
        hidealldeliverables: hideAllDeliverables,
        };
      });

      const mergeUniqueById = (existing: ProjectRow[], incoming: ProjectRow[]) => {
        const byId = new Map(existing.map((r) => [String(r.id), r] as const));
        incoming.forEach((r) => {
          const id = String(r.id);
          if (!byId.has(id)) byId.set(id, r);
        });
        return Array.from(byId.values());
      };

      // Save original state for tracking changes (ref to avoid extra re-render).
      // Important: when paging, keep previously-loaded originals instead of overwriting.
      const mappedClone: ProjectRow[] = JSON.parse(JSON.stringify(mapped));

      if (projectsLoadSeqRef.current !== seq) return;

      // Track last fetch size so we can disable "Load more" when < page size.
      // For a force-reload we fetched multiple pages at once, so treat the
      // last-page size as the relevant value.
      if (force) {
        const effectivePages = Math.max(1, Math.ceil(limit / pageSize));
        const lastPageCount = Math.max(0, mapped.length - (effectivePages - 1) * pageSize);
        setLastProjectsFetchCount(lastPageCount);

        // When force-reloading, do not overwrite the baseline for existing rows
        // (it can invalidate dirty tracking). Only add baseline entries for any
        // new rows that weren't previously tracked.
        const baselineById = new Map(
          (originalRowsRef.current || []).map((r) => [String(r.id), r]),
        );
        mappedClone.forEach((r) => {
          const id = String(r.id);
          if (!baselineById.has(id)) baselineById.set(id, r);
        });
        originalRowsRef.current = Array.from(baselineById.values());

        // Update existing rows in-place (by id) so the grid actually refreshes.
        // Preserve locally edited visibility fields; refresh server-driven
        // fields that should reflect the latest status settings.
        setRows((prev) => {
          if (!prev || prev.length === 0) return mapped;

          const managersSig = (managers?: ProjectPerson[]) =>
            (managers || [])
              .map(
                (m) =>
                  `${String((m as any)?.id ?? "").trim().toLowerCase()}|${String(
                    (m as any)?.email ?? "",
                  )
                    .trim()
                    .toLowerCase()}|${String((m as any)?.name ?? "").trim()}|${String(
                    (m as any)?.visibility ?? "",
                  )}`,
              )
              .join(",");

          const incomingById = new Map(
            mapped.map((r) => [String(r.id), r] as const),
          );
          const prevIds = new Set(prev.map((r) => String(r.id)));
          const shouldPrune = offset === 0 && limit >= (rowsRef.current?.length ?? prev.length);

          let anyChanged = false;
          const next = prev
            .filter((row) => {
              if (!shouldPrune) return true;
              return incomingById.has(String(row.id));
            })
            .map((row) => {
              const inc = incomingById.get(String(row.id));
              if (!inc) return row;

              const engagementManagersChanged =
                managersSig(row.engagementManagers) !==
                managersSig(inc.engagementManagers);

              // Only replace fields that are not edited locally.
              const changed =
                row.status !== inc.status ||
                row.haveUnreadMessage !== inc.haveUnreadMessage ||
                row.haveUnreadNote !== inc.haveUnreadNote ||
                row.lastMessageCreated !== inc.lastMessageCreated ||
                row.lastMessageCreatedBy !== inc.lastMessageCreatedBy ||
                row.lastNoteCreated !== inc.lastNoteCreated ||
                row.lastNoteCreatedBy !== inc.lastNoteCreatedBy ||
                engagementManagersChanged;

              // Always keep local edits / detail caches.
              if (!changed) return row;
              anyChanged = true;
              return {
                ...row,
                clientId: inc.clientId,
                client: inc.client,
                projectDefaultName: inc.projectDefaultName,
                project: inc.project,
                projectCustomName: inc.projectCustomName,
                consent7216: inc.consent7216,
                taxYear: inc.taxYear,
                projectIdMdm: inc.projectIdMdm,
                projectIdTim: inc.projectIdTim,
                engagementManagers: inc.engagementManagers,
                status: inc.status,
                haveUnreadMessage: inc.haveUnreadMessage,
                haveUnreadNote: inc.haveUnreadNote,
                lastMessageCreated: inc.lastMessageCreated,
                lastMessageCreatedBy: inc.lastMessageCreatedBy,
                lastNoteCreated: inc.lastNoteCreated,
                lastNoteCreatedBy: inc.lastNoteCreatedBy,
              };
            });

          const extras = mapped.filter((r) => !prevIds.has(String(r.id)));
          if (extras.length > 0) {
            anyChanged = true;
            return next.concat(extras);
          }

          return anyChanged ? next : prev;
        });
      } else {
        setLastProjectsFetchCount(mapped.length);

        if (requestedPage === 1) {
          originalRowsRef.current = mappedClone;
        } else {
          const existingById = new Map(
            (originalRowsRef.current || []).map((r) => [String(r.id), r]),
          );
          mappedClone.forEach((r) => {
            const id = String(r.id);
            if (!existingById.has(id)) existingById.set(id, r);
          });
          originalRowsRef.current = Array.from(existingById.values());
        }

        setRows((prev) =>
          requestedPage === 1 ? mapped : mergeUniqueById(prev, mapped),
        );
      }
      // Only the first page reliably returns the correct total.
      // Preserve the captured total for later pages so pagination stays accurate.
      setTotalCount((prevTotal) =>
        force || requestedPage === 1 ? total : prevTotal,
      );
      if (force || requestedPage === 1) {
        setHiddenProjectIdsForCount(hiddenProjectIds);
        // Reset orphan caches — entities/deliverables will be re-fetched on next expand.
        setEntityOrphanHiddenIdsByProject({});
        setDeliverableOrphanHiddenIdsByProject({});
      }
    } catch (err) {
      // If a "Load more" request fails, keep current data and roll back
      // the page increment so the user can retry without skipping records.
      if (projectsLoadSeqRef.current === seq) {
        if (!force && requestedPage > 1) {
          setPage(requestedPage - 1);
        } else if (!force) {
          setRows([]);
          setTotalCount(0);
          setHiddenProjectIdsForCount([]);
        }
      }
    } finally {
      // Only clear pending for the latest request
      if (projectsLoadSeqRef.current === seq) {
        setPending(false);
        // Keep the spinner while a pending view switch hasn't started its own reload yet.
        if (!viewChangeReloadPendingRef.current) setViewSwitching(false);
      }
    }
  }, [page, pageSize, getCurrentUserEmail]);

  const isDirty = useMemo(() => {
    void dirtyBaselineVersion;
    // Column chooser changes (hiddenColumns) are persisted immediately to
    // sessionStorage and intentionally excluded from dirty tracking below,
    // since they require no API call and shouldn't enable Save Changes.
    const origRows = originalRowsRef.current;
    return rows.some(row => {
      const orig = origRows.find(r => r.id === row.id);
      if (!orig) return false;
      if (row.visibility !== orig.visibility) return true;

      if (!!row.hideallentities !== !!orig.hideallentities) return true;
      if (!!row.hidealldeliverables !== !!orig.hidealldeliverables) return true;

      const origEnts = orig.entities || [];
      if ((row.entities || []).some(e => {
        const oe = origEnts.find(x => x.id === e.id);
        return oe && e.visibility !== oe.visibility;
      })) return true;
      const origDels = orig.deliverables || [];
      if ((row.deliverables || []).some(d => {
        const od = origDels.find(x => x.id === d.id);
        return od && d.visibility !== od.visibility;
      })) return true;
      return false;
    });
  }, [rows, dirtyBaselineVersion]);

  useEffect(() => {
    isDirtyRef.current = isDirty || tasksIsDirty;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty && !tasksIsDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, tasksIsDirty]);

  // Show the leave-page dialog when the user clicks outside the iframe / switches tabs
  // while there are unsaved changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setConfirmLeavePageOpen(false);
      }
    };

    const handleBlur = () => {
      if (document.visibilityState === "hidden") return;
      if (isDirtyRef.current) {
        setConfirmLeavePageOpen(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);


  useEffect(() => {
    if (blocker.state === "blocked") {
      setConfirmLeavePageOpen(true);
    }
  }, [blocker.state]);

  const revertUnsavedChanges = useCallback(() => {
    const originalHidden = originalHiddenColumnsRef.current || [];
    setHiddenColumns([...originalHidden]);
    applyGridColumnVisibility(originalHidden);
    const originalRows = originalRowsRef.current || [];
    const dirtyProjectIds = rows
      .filter((r) => {
        const orig = originalRows.find((o) => o.id === r.id);
        return !!orig && orig.visibility !== r.visibility;
      })
      .map((r) => r.id);
    setRows(originalRows.map((r) => ({ ...r })));
    syncVisibilityRefsFromRows(originalRows.map((r) => ({ ...r })));
    try {
      entitiesDirtyByProjectRef.current.clear();
      deliverablesDirtyByProjectRef.current.clear();
    } catch {}
    requestAnimationFrame(() => {
      const inst = gridRef.current?.instance;
      try { inst?.option("dataSource", projectsGridDataSourceRef.current); } catch {}
      try {
        if (inst && dirtyProjectIds.length) {
          const indexes = dirtyProjectIds
            .map((id) => inst.getRowIndexByKey(id))
            .filter((idx: number) => typeof idx === "number" && idx >= 0);
          if (indexes.length) inst.repaintRows(indexes);
        }
      } catch {}
      // Nested grids' cellRender reads the visibility-map refs above, but their own
      // dataSource memo won't recompute on a same-length revert, so repaint them
      // directly (same scoped pattern the bulk hide/show-all handlers already use).
      try {
        Object.values(entityGridInstancesRef.current).forEach((gi: any) => { try { gi?.repaint(); } catch {} });
        Object.values(deliverableGridInstancesRef.current).forEach((gi: any) => { try { gi?.repaint(); } catch {} });
      } catch {}
      notifyAllProjectDetailsChanged();
    });
  }, [applyGridColumnVisibility, notifyAllProjectDetailsChanged, rows, syncVisibilityRefsFromRows]);

  const handleConfirmLeavePage = useCallback(() => {
    setConfirmLeavePageOpen(false);
    revertUnsavedChanges();
    if (tasksIsDirty) {
      tasksCancelRef.current?.();
      setTasksIsDirty(false);
    }
    const pendingViewSwitch = pendingViewSwitchRef.current;
    pendingViewSwitchRef.current = null;
    if (pendingViewSwitch) {
      setActiveView(pendingViewSwitch);
      return;
    }
    blocker.proceed?.();
  }, [blocker, revertUnsavedChanges, tasksIsDirty]);

  const handleCancelLeavePage = useCallback(() => {
    setConfirmLeavePageOpen(false);
    pendingViewSwitchRef.current = null;
    blocker.reset?.();
  }, [blocker]);

  const handleTopLevelViewChange = useCallback((view: 'project' | 'task') => {
    if (view === activeView) return;
    if (activeView === 'task' && tasksIsDirty) {
      pendingViewSwitchRef.current = view;
      setConfirmLeavePageOpen(true);
      return;
    }
    setActiveView(view);
  }, [activeView, tasksIsDirty]);

  // The Projects grid stays mounted (display: none) while Tasks is shown. When StateStoring re-applies
  // the stored grid state there (it re-runs customLoad whenever projectsDataGrid is recomputed), virtual
  // column rendering lays the grid out at zero width and it comes back with no columns. Rebuild it only
  // in that case (repaint restores the columns, refresh the cell values), so expanded projects are
  // otherwise left untouched when switching back.
  useEffect(() => {
    if (activeView !== 'project') return;
    const frame = requestAnimationFrame(() => {
      const inst = gridRef.current?.instance;
      if (!inst) return;
      try {
        const root = inst.element();
        const el: HTMLElement | undefined = root?.get ? root.get(0) : root;
        const headerCells = el?.querySelectorAll('.dx-datagrid-headers .dx-header-row > td').length ?? 0;
        if (inst.getVisibleColumns().length > 1 && headerCells <= 1) {
          inst.repaint();
          inst.refresh();
        }
      } catch {}
    });
    return () => cancelAnimationFrame(frame);
  }, [activeView]);

  const isSettingsDirty = useMemo(() => {
    const orig = originalProjectSettingsRef.current;
    if (orig.length !== projectSettingsData.length) return orig.length > 0 || projectSettingsData.length > 0;
    return projectSettingsData.some(p => {
      const o = orig.find(x => x.key === p.key);
      return !o || o.visibility !== p.visibility || o.note !== p.note;
    });
  }, [projectSettingsData]);

  const isStatusDirty = useMemo(() => {
    const orig = originalStatusManagementRef.current;
    if (orig.length !== statusManagementData.length) return orig.length > 0 || statusManagementData.length > 0;
    return statusManagementData.some(s => {
      const o = orig.find(x => x.id === s.id);
      return !o || o.customStatus !== s.customStatus || o.description !== s.description || o.isVisible !== s.isVisible;
    });
  }, [statusManagementData]);

  const searchedRows = useMemo(() => {
    const q = (debouncedSearch || "").trim().toLowerCase();
    if (q.length === 0) return rows;
    return rows.filter(
      (r) =>
        r.client.toLowerCase().includes(q) ||
        getProjectDisplayName(r).toLowerCase().includes(q) ||
        String(r.clientId).includes(q) ||
        String(r.taxYear).includes(q) ||
        String(r.status || "").toLowerCase().includes(q) ||
        (r.engagementManagers || []).some((manager) => {
          const name = String(manager?.name || "").toLowerCase();
          const email = String(manager?.email || "").toLowerCase();
          const emp = String((manager as any)?.employeenumber || "").toLowerCase();
          return name.includes(q) || email.includes(q) || emp.includes(q);
        }),
    );
  }, [rows, debouncedSearch]);

  // API: Entities loader with required parameters
  const fetchEntitiesFromApi = useCallback(async (
    siteId: number,
    projectIdMdm: number,
    projectIdTim: number,
    taxYear: number,
    offset: number = 0,
    limit: number = SUBGRID_PAGE_SIZE,
    signal?: AbortSignal,
  ): Promise<{ items: EntityRow[]; total: number; allHiddenEntityIds: number[] }> => {
    const resp = await createSitesClientApi.tim_Entities(
      siteId,
      projectIdMdm,
      projectIdTim,
      offset,
      limit,
      isClientViewRef.current,
      signal,
    );
    const payload =
      resp && (resp as any).data?.data ? (resp as any).data?.data : resp;
    const entities = (payload && (payload as any).entities) || [];
    const totalRaw =
      (payload && ((payload as any).totalcount ?? (payload as any).totalCount)) ??
      (entities as any[]).length;
    const totalCoerced =
      typeof totalRaw === "number" ? totalRaw : Number(totalRaw);
    const total =
      typeof totalCoerced === "number" && Number.isFinite(totalCoerced)
        ? totalCoerced
        : (entities as any[]).length;

    const items = (entities as any[]).map((e, idx) => ({
      id: e.entityid ?? idx,
      entityId: e.entityid ?? 0,
      entity: e.entity ?? "",
      legalEntityType: e.legalentitytype ?? undefined,
      consent7216: e.consent7216 ?? undefined,
      imanageUrl: e.imanagelink ?? undefined,
      visibility: !!e.visibility,
    }));
    const hiddenEntityRaw = (payload as any)?.hiddenentityids ?? (payload as any)?.hiddenEntityIds ?? [];
    const allHiddenEntityIds: number[] = Array.isArray(hiddenEntityRaw)
      ? hiddenEntityRaw.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id) && id > 0)
      : [];
    return { items, total, allHiddenEntityIds };
  }, []);

  const fetchDeliverablesFromApi = useCallback(async (
    siteId: number,
    projectIdMdm: number,
    projectIdTim: number,
    taxYear: number,
    offset: number = 0,
    limit: number = SUBGRID_PAGE_SIZE,
    signal?: AbortSignal,
  ): Promise<{ items: DeliverableRow[]; total: number; allHiddenDeliverableIds: number[] }> => {
    const resp = await createSitesClientApi.tim_Deliverables(
      siteId,
      projectIdMdm,
      projectIdTim,
      offset,
      limit,
      isClientViewRef.current,
      signal,
    );
    const payload =
      resp && (resp as any).data?.data ? (resp as any).data?.data : resp;
    const list = (payload && (payload as any).deliverables) || [];
    const totalRaw =
      (payload && ((payload as any).totalcount ?? (payload as any).totalCount)) ??
      (list as any[]).length;
    const totalCoerced =
      typeof totalRaw === "number" ? totalRaw : Number(totalRaw);
    const total =
      typeof totalCoerced === "number" && Number.isFinite(totalCoerced)
        ? totalCoerced
        : (list as any[]).length;

    const fmt = (d?: any) =>
      d
        ? new Date(d).toLocaleDateString(undefined, {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
        : undefined;
    const items = (list as any[]).map((d) => {
      return {
        id: d.id ?? `${projectIdTim}-${d.entityid ?? ""}-${d.form ?? ""}`,
        entityId: d.entityid ?? 0,
        entity: d.entity ?? "",
        jurisdiction: d.jurisdictionname ?? undefined,
        form: d.form ?? undefined,
        status: d.status ?? undefined,
        internalDueDate: fmt(d.internalduedate),
        statutoryDueDate: fmt(d.statutoryduedate),
        extended: d.extended ?? undefined,
        filingMethod: d.filingmethod ?? undefined,
        deliveryMethod: d.deliverymethod ?? undefined,
        visibility: !!d.visibility,
      } as DeliverableRow;
    });
    const hiddenDeliverableRaw = (payload as any)?.hiddendeliverableids ?? (payload as any)?.hiddenDeliverableIds ?? [];
    const allHiddenDeliverableIds: number[] = Array.isArray(hiddenDeliverableRaw)
      ? hiddenDeliverableRaw.map((id: any) => Number(id)).filter((id: number) => Number.isFinite(id) && id > 0)
      : [];
    return { items, total, allHiddenDeliverableIds };
  }, []);

  const ensureEntitiesLoaded = useCallback((
    projectId: string,
    append: boolean = false,
    force: boolean = false,
    forceHiddenOverride?: boolean,
  ): Promise<void> => {
    const row = rowsRef.current.find((r) => r.id === projectId);
    const already = row?.entities;
    const forceHidden =
      typeof forceHiddenOverride === "boolean"
        ? forceHiddenOverride
        : !!row?.hideallentities;

    if (forceHidden && already !== undefined && already.some((e) => !!e.visibility)) {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== projectId) return r;
          // Do not force hide-all flag back on; only force item visibility.
          const ents = (r.entities || []).map((e) => ({ ...e, visibility: false }));
          return { ...r, entities: ents };
        }),
      );
    }

    if (!append && !force && already !== undefined) return Promise.resolve();
    // Synchronous in-flight guard (state can be stale within the same tick)
    if (entityAbortControllersRef.current[projectId]) return Promise.resolve();

    const loadedCount = append ? (already?.length ?? 0) : 0;
    const knownTotal = entityTotalByProjectRef.current[projectId];
    if (append && knownTotal !== undefined && loadedCount >= knownTotal) return Promise.resolve();

    setEntityLoadingMoreByProject((prev) => ({ ...prev, [projectId]: append }));
    const controller = new AbortController();
    entityAbortControllersRef.current[projectId] = controller;
    setEntityLoadingByProject((prev) => ({ ...prev, [projectId]: true }));
    setEntityLoadingMoreByProject((prev) => ({ ...prev, [projectId]: append }));

    const siteId = parseInt(sessionStorage.getItem("siteId")!);
    const projectIdMdm = row?.projectIdMdm ?? 0;
    const projectIdTim = row?.projectIdTim ? Number(row.projectIdTim) : 0;
    const taxYear = row?.taxYear ?? 0;

    return fetchEntitiesFromApi(
      siteId,
      projectIdMdm,
      projectIdTim,
      taxYear,
      loadedCount,
      SUBGRID_PAGE_SIZE,
      controller.signal,
    )
      .then(({ items, total, allHiddenEntityIds }) => {
        const serverItems = items || [];

        setEntityLastFetchCountByProject((prev) => ({
          ...prev,
          [projectId]: Array.isArray(items) ? items.length : 0,
        }));
        // Only trust totalcount on the first (offset=0) fetch for this subgrid.
        // Later pages can return inaccurate totals.
        setEntityTotalByProject((prev) => {
          const existing = prev[projectId];
          if (typeof existing === "number" && Number.isFinite(existing) && existing > 0) {
            return prev;
          }
          if (loadedCount !== 0) return prev;
          return { ...prev, [projectId]: total };
        });

        setRows((prev) =>
          prev.map((r) => {
            if (r.id !== projectId) return r;
            // Respect the *current* hide-all flag, not the value captured when the request started.
            // This prevents in-flight loads from flipping the user's local hide-all state.
            const shouldForceHidden =
              typeof forceHiddenOverride === "boolean"
                ? forceHiddenOverride
                : !!r.hideallentities;
            const incoming = shouldForceHidden
              ? serverItems.map((it) => ({ ...it, visibility: false }))
              : serverItems;

            const existing = r.entities || [];
            const merged = append
              ? (() => {
                  const byId = new Map(existing.map((e) => [String(e.id), e]));
                  const out = existing.slice();
                  for (const item of incoming || []) {
                    const key = String(item.id);
                    if (!byId.has(key)) {
                      byId.set(key, item);
                      out.push(item);
                    }
                  }
                  return out;
                })()
              : (incoming || []);
            return { ...r, entities: merged };
          }),
        );

        originalRowsRef.current = originalRowsRef.current.map((r) => {
          if (r.id !== projectId) return r;
          // Preserve the baseline hide-all flag. Fetches can happen after the user
          // has toggled hide-all locally (e.g., Hide All -> Show All triggers a refresh).
          // We must not overwrite the baseline, otherwise Save/Cancel state breaks.
          const baselineHideAll = !!r.hideallentities;
          const existing = r.entities || [];
          const combined = append ? [...existing, ...serverItems] : serverItems;
          const effectiveItems = baselineHideAll
            ? combined.map((it) => ({ ...it, visibility: false }))
            : combined;
          const unique = Array.from(
            new Map(effectiveItems.map((item) => [String(item.id), item])).values(),
          );
          return { ...r, entities: unique };
        });

        // Track hidden IDs for records not yet loaded into the grid (orphan IDs).
        // On initial load: orphan = all API-reported hidden IDs minus those now in the grid.
        // On append: just remove newly loaded IDs from the existing orphan list.
        const loadedEntityIds = new Set(
          serverItems.map((e) => Number(e.entityId)).filter((id) => Number.isFinite(id) && id > 0),
        );
        if (!append) {
          setEntityOrphanHiddenIdsByProject((prev) => ({
            ...prev,
            [projectId]: allHiddenEntityIds.filter((id) => !loadedEntityIds.has(id)),
          }));
        } else {
          setEntityOrphanHiddenIdsByProject((prev) => ({
            ...prev,
            [projectId]: (prev[projectId] ?? []).filter((id) => !loadedEntityIds.has(id)),
          }));
        }
      })
      .catch((err) => {
        if (err && (err.name === "AbortError" || err.message === "aborted")) {
          return;
        }
        if (!append) {
          setRows((prev) =>
            prev.map((r) => (r.id === projectId ? { ...r, entities: [] } : r)),
          );
        }
      })
      .finally(() => {
        setEntityLoadingByProject((prev) => ({ ...prev, [projectId]: false }));
        setEntityLoadingMoreByProject((prev) => ({
          ...prev,
          [projectId]: false,
        }));
        delete entityAbortControllersRef.current[projectId];
        requestAnimationFrame(() => notifyProjectDetailChanged(projectId));
      });
  }, [
    fetchEntitiesFromApi,
    notifyProjectDetailChanged,
  ]);

  const ensureDeliverablesLoaded = useCallback((
    projectId: string,
    append: boolean = false,
    force: boolean = false,
    forceHiddenOverride?: boolean,
  ): Promise<void> => {
    const row = rowsRef.current.find((r) => r.id === projectId);
    const already = row?.deliverables;
    const forceHidden =
      typeof forceHiddenOverride === "boolean"
        ? forceHiddenOverride
        : !!row?.hidealldeliverables;

    if (forceHidden && already !== undefined && already.some((d) => !!d.visibility)) {
      setRows((prev) =>
        prev.map((r) => {
          if (r.id !== projectId) return r;
          const dels = (r.deliverables || []).map((d) => ({ ...d, visibility: false }));
          return { ...r, deliverables: dels };
        }),
      );
    }

    if (!append && !force && already !== undefined) return Promise.resolve();
    if (deliverableAbortControllersRef.current[projectId]) return Promise.resolve();

    const loadedCount = append ? (already?.length ?? 0) : 0;
    const knownTotal = deliverableTotalByProjectRef.current[projectId];
    if (append && knownTotal !== undefined && loadedCount >= knownTotal) return Promise.resolve();

    const controller = new AbortController();
    deliverableAbortControllersRef.current[projectId] = controller;
    setDeliverableLoadingByProject((prev) => ({ ...prev, [projectId]: true }));
    setDeliverableLoadingMoreByProject((prev) => ({
      ...prev,
      [projectId]: append,
    }));

    const siteId = parseInt(sessionStorage.getItem("siteId")!);
    const projectIdMdm = row?.projectIdMdm ?? 0;
    const projectIdTim = row?.projectIdTim ? Number(row.projectIdTim) : 0;
    const taxYear = row?.taxYear ?? 0;

    return fetchDeliverablesFromApi(
      siteId,
      projectIdMdm,
      projectIdTim,
      taxYear,
      loadedCount,
      SUBGRID_PAGE_SIZE,
      controller.signal,
    )
      .then(({ items, total, allHiddenDeliverableIds }) => {
        const serverItems = items || [];

        setDeliverableLastFetchCountByProject((prev) => ({
          ...prev,
          [projectId]: Array.isArray(items) ? items.length : 0,
        }));
        // Only trust totalcount on the first (offset=0) fetch for this subgrid.
        setDeliverableTotalByProject((prev) => {
          const existing = prev[projectId];
          if (typeof existing === "number" && Number.isFinite(existing) && existing > 0) {
            return prev;
          }
          if (loadedCount !== 0) return prev;
          return { ...prev, [projectId]: total };
        });

        setRows((prev) =>
          prev.map((r) => {
            if (r.id !== projectId) return r;
            const shouldForceHidden =
              typeof forceHiddenOverride === "boolean"
                ? forceHiddenOverride
                : !!r.hidealldeliverables;
            const incoming = shouldForceHidden
              ? serverItems.map((it) => ({ ...it, visibility: false }))
              : serverItems;

            const existing = r.deliverables || [];
            const merged = append
              ? (() => {
                  const byId = new Map(existing.map((d) => [String(d.id), d]));
                  const out = existing.slice();
                  for (const item of incoming || []) {
                    const key = String(item.id);
                    if (!byId.has(key)) {
                      byId.set(key, item);
                      out.push(item);
                    }
                  }
                  return out;
                })()
              : (incoming || []);
            return { ...r, deliverables: merged };
          }),
        );

        originalRowsRef.current = originalRowsRef.current.map((r) => {
          if (r.id !== projectId) return r;
          // Preserve the baseline hide-all flag for the same reason as entities.
          const baselineHideAll = !!r.hidealldeliverables;
          const existing = r.deliverables || [];
          const combined = append ? [...existing, ...serverItems] : serverItems;
          const effectiveItems = baselineHideAll
            ? combined.map((it) => ({ ...it, visibility: false }))
            : combined;
          const unique = Array.from(
            new Map(effectiveItems.map((item) => [String(item.id), item])).values(),
          );
          return { ...r, deliverables: unique };
        });

        const loadedDeliverableIds = new Set(
          serverItems.map((d) => Number(d.id)).filter((id) => Number.isFinite(id) && id > 0),
        );
        if (!append) {
          setDeliverableOrphanHiddenIdsByProject((prev) => ({
            ...prev,
            [projectId]: allHiddenDeliverableIds.filter((id) => !loadedDeliverableIds.has(id)),
          }));
        } else {
          setDeliverableOrphanHiddenIdsByProject((prev) => ({
            ...prev,
            [projectId]: (prev[projectId] ?? []).filter((id) => !loadedDeliverableIds.has(id)),
          }));
        }
      })
      .catch((err) => {
        if (err && (err.name === "AbortError" || err.message === "aborted")) {
          return;
        }
        if (!append) {
          setRows((prev) =>
            prev.map((r) =>
              r.id === projectId ? { ...r, deliverables: [] } : r,
            ),
          );
        }
      })
      .finally(() => {
        setDeliverableLoadingByProject((prev) => ({
          ...prev,
          [projectId]: false,
        }));
        setDeliverableLoadingMoreByProject((prev) => ({
          ...prev,
          [projectId]: false,
        }));
        delete deliverableAbortControllersRef.current[projectId];
        requestAnimationFrame(() => notifyProjectDetailChanged(projectId));
      });
  }, [
    fetchDeliverablesFromApi,
    notifyProjectDetailChanged,
  ]);

  const initializeVisibility = useCallback(async () => {
    try {
      setLoadingVisibility(true);
      var siteId = parseInt(sessionStorage.getItem("siteId")!);
      const response = await createSitesClientApi.tim_GetProjectVisibilty(
        siteId,
      );

      // API can return either legacy shape { id, value } or the newer shape:
      // { hqsiteid, showproject: boolean, isclientuser: boolean }
      const outer: any =
        (response as any)?.data?.data ?? (response as any)?.data ?? response;
      // Some endpoints wrap with { success, data: {...} }
      const payload: any = outer?.data ?? outer;

      const id = payload?.id ?? outer?.id ?? null;
      const showProjectRaw =
        payload?.showproject ??
        payload?.showProject ??
        outer?.showproject ??
        outer?.showProject ??
        payload?.value ??
        outer?.value ??
        false;
      const showProject =
        typeof showProjectRaw === "string"
          ? showProjectRaw.toLowerCase() === "true"
          : !!showProjectRaw;

      const clientUserRaw =
        payload?.isclientuser ??
        payload?.isClientUser ??
        payload?.isclient ??
        outer?.isclientuser ??
        outer?.isClientUser ??
        outer?.isclient ??
        false;
      const clientUser =
        typeof clientUserRaw === "string"
          ? clientUserRaw.toLowerCase() === "true"
          : !!clientUserRaw;

      // For true client logins, fetch settings now so the Tasks tab's visibility
      // is known before the first render — getUiForProjectSettings is defined above this fn.
      if (clientUser) {
        try {
          const parsed = await getUiForProjectSettings();
          if (parsed) {
            setClientDetailsEnabled((prev) => ({ ...prev, tasks: parsed?.Task === true }));
          }
        } catch {}
      }

      setVisibilityId(id);
      setIsClientUser(clientUser);
      setIsClientView(clientUser);
      setEffectiveShowProjects(showProject);
      setDraftShowProjects(showProject);

      return { clientUser, showProject };
    } catch (error) {
      console.error("Failed to fetch project visibility:", error);
      setIsClientUser(false);
      setIsClientView(false);
      setEffectiveShowProjects(false);
      setDraftShowProjects(false);
      return null;
    } finally {
      setLoadingVisibility(false);
      setHasDeterminedUserType(true);
    }
  }, [getUiForProjectSettings]);

  useEffect(() => {
    try {
      // Prefer localStorage for persisted hidden columns, migrate from sessionStorage if present
      const localRaw = localStorage.getItem("projectsGrid_hiddenColumns_v2");
      const sessionRaw = sessionStorage.getItem("projectsGrid_hiddenColumns_v2");
      const raw = localRaw ?? sessionRaw ?? "[]";
      const storedHidden = JSON.parse(raw);
      if (Array.isArray(storedHidden)) {
        // If migration needed (session present but local missing), copy it
        if (!localRaw && sessionRaw) {
          try { localStorage.setItem("projectsGrid_hiddenColumns_v2", JSON.stringify(storedHidden)); } catch {}
        }
        originalHiddenColumnsRef.current = storedHidden;
        setHiddenColumns(storedHidden);
        applyGridColumnVisibility(storedHidden);
      }
    } catch {}

    // Do not load the grid/data until we know whether the current user is a
    // client user (from tim_GetProjectVisibilty).
    let cancelled = false;
    (async () => {
      try {
        const visibility = await initializeVisibility();
        if (cancelled) return;

        // If this is a true client user and projects are not enabled globally,
        // skip loading project data entirely.
        if (visibility?.clientUser && !visibility.showProject) {
          setRows([]);
          setTotalCount(0);
          return;
        }
      } finally {
        if (!cancelled) {
          setHasBootstrapped(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applyGridColumnVisibility, initializeVisibility]);

  useEffect(() => {
    if (!isClientView) {
      setIsClientViewTransitionLoading(false);
      applyGridColumnVisibility(hiddenColumns);
      return;
    }

    setIsClientViewTransitionLoading(true);

    // Apply best-known client columns immediately to avoid showing internal
    // view columns while client-specific settings are being fetched.
    try {
      const hiddenSet = new Set<string>(DEFAULT_CLIENT_HIDDEN_COLUMNS);

      if (projectSettingsData.length > 0) {
        projectSettingsData.forEach((setting) => {
          const columnName = CLIENT_SETTING_TO_COLUMN_NAME_MAP[setting.key];
          if (!columnName) return;
          if (setting.visibility === true) hiddenSet.delete(columnName);
          else hiddenSet.add(columnName);
        });
      } else if (uiProjectSettingsCacheRef.current?.parsed) {
        const parsed = uiProjectSettingsCacheRef.current.parsed;
        Object.keys(CLIENT_SETTING_TO_COLUMN_NAME_MAP).forEach((key) => {
          const columnName = CLIENT_SETTING_TO_COLUMN_NAME_MAP[key];
          const settingValue = parsed?.[key];
          if (settingValue === true) hiddenSet.delete(columnName);
          else hiddenSet.add(columnName);
        });
      }

      const immediate = Array.from(hiddenSet);
      clientHiddenColumnsRef.current = immediate;
      setClientHiddenColumns(immediate);
      applyGridColumnVisibility(immediate);
    } catch {
      clientHiddenColumnsRef.current = DEFAULT_CLIENT_HIDDEN_COLUMNS;
      applyGridColumnVisibility(DEFAULT_CLIENT_HIDDEN_COLUMNS);
    }

    let cancelled = false;
    (async () => {
      try {
        const clientHiddenColumns = await fetchClientHiddenColumns();
        if (!cancelled) {
          clientHiddenColumnsRef.current = clientHiddenColumns;
          setClientHiddenColumns(clientHiddenColumns);
          applyGridColumnVisibility(clientHiddenColumns);
        }
      } finally {
        if (!cancelled) {
          setIsClientViewTransitionLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isClientView, isClientUser, hiddenColumns, projectSettingsData, fetchClientHiddenColumns, applyGridColumnVisibility]);

  useEffect(() => {
    // When grid is remounted after client-view loading, re-apply client columns
    // so hidden columns do not appear temporarily.
    if (!isClientView) return;
    if (isClientViewTransitionLoading || pending) return;
    applyGridColumnVisibility(clientHiddenColumns);
  }, [
    isClientView,
    isClientViewTransitionLoading,
    pending,
    clientHiddenColumns,
    applyGridColumnVisibility,
  ]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setDebouncedSearch(search);
    }, 250);

    return () => {
      window.clearTimeout(timer);
    };
  }, [search]);

  useEffect(() => {
    if (suppressNextEffectRef.current) {
      suppressNextEffectRef.current = false;
      return;
    }
    if (skipNextProjectsLoadRef.current) {
      skipNextProjectsLoadRef.current = false;
      return;
    }
    if (!hasBootstrapped) {
      return;
    }
    // True client users must not load/see projects when global visibility is off.
    const canLoadProjects = !isClientUser || !!effectiveShowProjects;
    if (!canLoadProjects) {
      return;
    }
    // Always load data in engagement view
    loadData();
  }, [page, debouncedSearch, loadData, hasBootstrapped, isClientUser, effectiveShowProjects]);

  // Re-fetch projects (and drop cached entities/deliverables) whenever the user
  // switches between internal and client view, so the API is called with the
  // matching isClientView flag.
  useEffect(() => {
    if (!viewChangeReloadPendingRef.current) return;
    viewChangeReloadPendingRef.current = false;

    Object.values(entityAbortControllersRef.current).forEach((c) => { try { c?.abort(); } catch {} });
    Object.values(deliverableAbortControllersRef.current).forEach((c) => { try { c?.abort(); } catch {} });
    entityAbortControllersRef.current = {};
    deliverableAbortControllersRef.current = {};
    setEntityLoadingByProject({});
    setDeliverableLoadingByProject({});
    setEntityLoadingMoreByProject({});
    setDeliverableLoadingMoreByProject({});
    setEntityTotalByProject({});
    setDeliverableTotalByProject({});
    setEntityLastFetchCountByProject({});
    setDeliverableLastFetchCountByProject({});
    setEntityOrphanHiddenIdsByProject({});
    setDeliverableOrphanHiddenIdsByProject({});
    try {
      entitiesDirtyByProjectRef.current.clear();
      deliverablesDirtyByProjectRef.current.clear();
    } catch {}
    try { gridRef.current?.instance?.collapseAll(-1); } catch {}

    if (!hasBootstrapped) { setViewSwitching(false); return; }
    if (isClientUser && !effectiveShowProjects) { setViewSwitching(false); return; }

    if (page !== 1) {
      // The page change re-creates loadData and the effect above reloads page 1.
      setPage(1);
    } else {
      void loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientView]);

  const [confirmShowDialogOpen, setConfirmShowDialogOpen] = useState(false);
  const [confirmHideDialogOpen, setConfirmHideDialogOpen] = useState(false);

  const handleToggleVisibilityBanner = async (checked: boolean) => {
    // Show confirmation when turning ON visibility
    if (checked && !effectiveShowProjects) {
      setConfirmShowDialogOpen(true);
      return;
    }
    
    // Show confirmation when turning OFF visibility
    if (!checked && effectiveShowProjects) {
      setConfirmHideDialogOpen(true);
      return;
    }
  };

  const handleConfirmShowProjects = async () => {
    try {
      setSavingVisibility(true);
      var siteId = parseInt(sessionStorage.getItem("siteId")!);
      await createSitesClientApi.tim_UpdateProjectVisibilty(
        siteId,
        true
      );

      // Do not trigger a projects reload due to this toggle.
      skipNextProjectsLoadRef.current = true;

      setConfirmShowDialogOpen(false);
      setDraftShowProjects(true);
      setEffectiveShowProjects(true);
      setShowTempBanner(true);
    } catch (error) {
      console.error("Failed to update project visibility:", error);
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleCancelShowProjects = () => {
    setConfirmShowDialogOpen(false);
    setDraftShowProjects(false);
  };

  const handleConfirmHideProjects = async () => {
    try {
      setSavingVisibility(true);
      const siteId = parseInt(sessionStorage.getItem("siteId")!);
      await createSitesClientApi.tim_UpdateProjectVisibilty(siteId, false);

      // Do not trigger a projects reload due to this toggle.
      skipNextProjectsLoadRef.current = true;

      setConfirmHideDialogOpen(false);
      setDraftShowProjects(false);
      setEffectiveShowProjects(false);
      setShowTempBanner(false);
    } catch (error) {
      console.error("Failed to update project visibility:", error);
      alert("Failed to update project visibility. Please try again.");
    } finally {
      setSavingVisibility(false);
    }
  };

  const handleCancelHideProjects = () => {
    setConfirmHideDialogOpen(false);
    // Keep toggle ON if user cancels turning visibility off.
    setDraftShowProjects(true);
  };

  const repaintProjectRow = useCallback((projectId: string) => {
    const inst = gridRef.current?.instance;
    if (!inst || !projectId) return;
    try {
      let rowIndex = inst.getRowIndexByKey(projectId);
      if (!(typeof rowIndex === "number" && rowIndex >= 0)) {
        const visibleRows = inst.getVisibleRows?.() || [];
        const found = visibleRows.findIndex((r: any) => String(r?.key) === String(projectId));
        if (found >= 0) rowIndex = found;
      }
      if (typeof rowIndex === "number" && rowIndex >= 0) {
        inst.repaintRows([rowIndex]);
      }
      // Deliberately no full inst.repaint() fallback here: it redraws the whole grid
      // and resets scroll position, causing a visible flash on every toggle.
    } catch {}
  }, []);

  const handleRowVisibilityToggle = useCallback((
    row: ProjectRow,
    checked: boolean,
  ) => {
    projectVisibilityRef.current[row.id] = checked;
    // Mutate the row object DevExtreme already holds (same reference bound to the
    // grid's dataSource) so its internal data is correct without reassigning the
    // dataSource array, which would trigger a reload-like reset of scroll position.
    const boundRow = projectsGridDataSourceRef.current.find((r) => r.id === row.id);
    if (boundRow) {
      boundRow.visibility = checked;
      const cacheEntry = projectsGridRowCacheRef.current.get(row.id);
      if (cacheEntry) cacheEntry.sig = buildProjectRowSig(boundRow);
    }

    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === row.id);
      if (idx < 0) return prev;
      if (prev[idx].visibility === checked) return prev;
      const next = prev.slice();
      next[idx] = { ...prev[idx], visibility: checked };
      return next;
    });

    // Repaint only the changed row to avoid full-grid refresh/flicker.
    requestAnimationFrame(() => repaintProjectRow(row.id));
  }, [repaintProjectRow]);

  const registerEntityGridInstance = useCallback((projectId: string, instance: any) => {
    entityGridInstancesRef.current[projectId] = instance;
  }, []);
  const registerDeliverableGridInstance = useCallback((projectId: string, instance: any) => {
    deliverableGridInstancesRef.current[projectId] = instance;
  }, []);

  const repaintNestedRow = (inst: any, itemId: number | string) => {
    if (!inst) return;
    try {
      let rowIndex = inst.getRowIndexByKey(itemId);
      if (!(typeof rowIndex === "number" && rowIndex >= 0)) {
        const visibleRows = inst.getVisibleRows?.() || [];
        const found = visibleRows.findIndex((r: any) => String(r?.key) === String(itemId));
        if (found >= 0) rowIndex = found;
      }
      if (typeof rowIndex === "number" && rowIndex >= 0) {
        inst.repaintRows([rowIndex]);
      }
      // No full inst.repaint() fallback: it would reset the nested grid's scroll and flash.
    } catch {}
  };

  const repaintEntityRow = useCallback((projectId: string, entityId: number | string) => {
    repaintNestedRow(entityGridInstancesRef.current[projectId], entityId);
  }, []);
  const repaintDeliverableRow = useCallback((projectId: string, deliverableId: number | string) => {
    repaintNestedRow(deliverableGridInstancesRef.current[projectId], deliverableId);
  }, []);

  const handleEntityVisibilityToggle = useCallback((
    projectId: string,
    entityId: number | string,
    checked: boolean,
  ) => {
    entitiesDirtyByProjectRef.current.add(projectId);
    entityVisibilityMapRef.current[projectId] = entityVisibilityMapRef.current[projectId] || {};
    entityVisibilityMapRef.current[projectId][String(entityId)] = checked;
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== projectId) return r;
        const targetId = String(entityId);
        const ents = (r.entities || []).map((e) =>
          String(e.id) === targetId ? { ...e, visibility: checked } : e,
        );

        // Rules:
        // - If hide-all is ON and user turns ON any single item => exit hide-all.
        // - If hide-all is OFF and user turns OFF the last visible item (all hidden) => enter hide-all,
        //   but ONLY when we can confirm the full list is loaded (avoid hiding unseen pages).
        const rawTotal = entityTotalByProjectRef.current[projectId];
        const total =
          typeof rawTotal === "number" && Number.isFinite(rawTotal)
            ? rawTotal
            : undefined;
        const fullListLoaded = total !== undefined && ents.length >= total;
        const allHidden = ents.length > 0 && ents.every((e) => !e.visibility);
        let nextHideAll = r.hideallentities;
        if (r.hideallentities && checked) {
          nextHideAll = false;
        } else if (!r.hideallentities && !checked && fullListLoaded && allHidden) {
          nextHideAll = true;
        }

        return { ...r, entities: ents, hideallentities: nextHideAll };
      }),
    );
    requestAnimationFrame(() => {
      repaintEntityRow(projectId, entityId);
      // Refresh the panel (Hide All label) via its own force-render, not a DevExtreme repaint.
      notifyProjectDetailChanged(projectId);
    });
  }, [repaintEntityRow, notifyProjectDetailChanged]);

  const handleBulkEntityVisibilityToggle = useCallback((projectId: string) => {
    const row = rowsRef.current.find((r) => r.id === projectId);
    if (!row) return;

    // Stop any in-flight load so it can't overwrite the user's local toggles.
    const inFlight = entityAbortControllersRef.current[projectId];
    if (inFlight) {
      try { inFlight.abort(); } catch {}
      delete entityAbortControllersRef.current[projectId];
      setEntityLoadingByProject((prev) => ({ ...prev, [projectId]: false }));
      setEntityLoadingMoreByProject((prev) => ({ ...prev, [projectId]: false }));
    }
    entitiesDirtyByProjectRef.current.add(projectId);

    const nextHideAll = !row.hideallentities;
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== projectId) return r;
        const nextEntities =
          r.entities === undefined
            ? undefined
            : nextHideAll
            ? r.entities.map((e) => ({ ...e, visibility: false }))
            : r.entities.map((e) => ({ ...e, visibility: true }));
        return { ...r, entities: nextEntities, hideallentities: nextHideAll };
      }),
    );
    // Bulk action touches every row in this project's entities grid; a scoped repaint
    // of just that nested instance is fine (doesn't affect the main grid or other projects).
    try { entityGridInstancesRef.current[projectId]?.repaint(); } catch {}
    // Pick up the new hideAllEntitiesState for the "Hide/Show All" label via the panel's
    // own force-render, not a DevExtreme repaint of the master-detail row.
    requestAnimationFrame(() => notifyProjectDetailChanged(projectId));
  }, [notifyProjectDetailChanged]);

  const handleDeliverableVisibilityToggle = useCallback((
    projectId: string,
    deliverableId: number | string,
    checked: boolean,
  ) => {
    deliverablesDirtyByProjectRef.current.add(projectId);
    deliverableVisibilityMapRef.current[projectId] = deliverableVisibilityMapRef.current[projectId] || {};
    deliverableVisibilityMapRef.current[projectId][String(deliverableId)] = checked;
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== projectId) return r;
        const targetId = String(deliverableId);
        const dels = (r.deliverables || []).map((d) =>
          String(d.id) === targetId ? { ...d, visibility: checked } : d,
        );

        const rawTotal = deliverableTotalByProjectRef.current[projectId];
        const total =
          typeof rawTotal === "number" && Number.isFinite(rawTotal)
            ? rawTotal
            : undefined;
        const fullListLoaded = total !== undefined && dels.length >= total;
        const allHidden = dels.length > 0 && dels.every((d) => !d.visibility);

        let nextHideAll = r.hidealldeliverables;
        if (r.hidealldeliverables && checked) {
          nextHideAll = false;
        } else if (!r.hidealldeliverables && !checked && fullListLoaded && allHidden) {
          nextHideAll = true;
        }

        return { ...r, deliverables: dels, hidealldeliverables: nextHideAll };
      }),
    );
    requestAnimationFrame(() => {
      repaintDeliverableRow(projectId, deliverableId);
      // Refresh the panel (Hide All label) via its own force-render, not a DevExtreme repaint.
      notifyProjectDetailChanged(projectId);
    });
  }, [repaintDeliverableRow, notifyProjectDetailChanged]);

  const handleBulkDeliverableVisibilityToggle = useCallback((projectId: string) => {
    const row = rowsRef.current.find((r) => r.id === projectId);
    if (!row) return;

    // Stop any in-flight load so it can't overwrite the user's local toggles.
    const inFlight = deliverableAbortControllersRef.current[projectId];
    if (inFlight) {
      try { inFlight.abort(); } catch {}
      delete deliverableAbortControllersRef.current[projectId];
      setDeliverableLoadingByProject((prev) => ({ ...prev, [projectId]: false }));
      setDeliverableLoadingMoreByProject((prev) => ({ ...prev, [projectId]: false }));
    }

    deliverablesDirtyByProjectRef.current.add(projectId);

    const nextHideAll = !row.hidealldeliverables;
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== projectId) return r;
        const nextDeliverables =
          r.deliverables === undefined
            ? undefined
            : nextHideAll
            ? r.deliverables.map((d) => ({ ...d, visibility: false }))
            : r.deliverables.map((d) => ({ ...d, visibility: true }));
        return { ...r, deliverables: nextDeliverables, hidealldeliverables: nextHideAll };
      }),
    );
    try { deliverableGridInstancesRef.current[projectId]?.repaint(); } catch {}
    requestAnimationFrame(() => notifyProjectDetailChanged(projectId));
  }, [notifyProjectDetailChanged]);

  const openActions = useCallback((
    event: React.MouseEvent<HTMLElement>,
    row: ProjectRow,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    // Use viewport coordinates so no anchor element is required.
    setActionsMenuPosition({ top: rect.bottom, left: rect.left });
    setActionRow(row);
  }, []);

  const actionsMenuAnchor = useMemo<VirtualElement | null>(() => {
    if (!actionsMenuPosition) return null;
    return {
      getBoundingClientRect: () =>
        new DOMRect(actionsMenuPosition.left, actionsMenuPosition.top, 0, 0),
    };
  }, [actionsMenuPosition]);

  const closeActions = useCallback(() => {
    setActionsMenuPosition(null);
    setActionRow(null);
  }, []);

  const handleOpenMessages = () => {
    if (actionRow) {
      const latestRow =
        rowsRef.current.find((r) => String(r.id) === String(actionRow.id)) ||
        actionRow;
      const siteId = parseInt(sessionStorage.getItem("siteId")!);
      if (siteId > 0 && latestRow.projectIdMdm) {
        upsertReadEntry(siteId, latestRow.projectIdMdm, {
          LastMessageReadTime:
            toIsoUtc(latestRow.lastMessageCreated) || new Date().toISOString(),
        });
      }
      // Update only this row so Unread icons disappear immediately without
      // forcing a full grid re-render (prevents flicker).
      repaintProjectRow(latestRow.id);

      setMessagesProject(latestRow);
      setIsMessagesOpen(true);
    }
    closeActions();
  };

  const handleCloseMessages = () => {
    setIsMessagesOpen(false);
    setMessagesProject(null);
  };

  const fetchProjectSettings = async (showLoader = true) => {
    if (showLoader) {
      setLoadingProjectSettings(true);
    }
    try {      
      var siteId = parseInt(sessionStorage.getItem("siteId")!);
      const resp = await createSitesClientApi.tim_GetProjectSettings(siteId);
      
      // Extract data from the API response
      const payload = (resp && (resp as any).data) ? (resp as any).data[0] : resp;
          
      // Store the ID for updates
      if (payload && payload.id) {
        setProjectSettingsId(payload.id);
      }
      
      // Parse the value field which contains JSON string
      let visibilitySettings: any = {};
      if (payload && payload.value) {
        try {
          visibilitySettings = JSON.parse(payload.value);
        } catch (e) {
          console.error('Failed to parse visibility settings:', e);
        }
      }

      // Keep the client-view settings cache in sync so toggling ClientView
      // reflects the latest saved settings (and the chevron behavior updates).
      uiProjectSettingsCacheRef.current = { siteId, parsed: visibilitySettings };
      
      // Map API response to the expected format
      const mappedProperties = Object.keys(visibilitySettings).map((key) => {
        const value = visibilitySettings[key];
        return {
          key: key,
          label: getProjectSettingLabel(key),
          note: typeof value === 'string' ? value : '',
          visibility: typeof value === 'boolean' ? value : true
        };
      });
      
      setProjectSettingsData(mappedProperties);
      originalProjectSettingsRef.current = mappedProperties.map(p => ({ ...p }));
    } catch (err) {
      console.error('Failed to fetch project settings:', err);
      setProjectSettingsData([]);
      originalProjectSettingsRef.current = [];
    } finally {
      if (showLoader) {
        setLoadingProjectSettings(false);
      }
    }
  };

  const fetchStatusManagementData = async (showLoader = true) => {
    if (showLoader) {
      setLoadingStatusData(true);
    }
    try {
      const siteId = parseInt(sessionStorage.getItem("siteId")!);
      const resp = await createSitesClientApi.tim_ProjectsCustomizationStatus(siteId);
               
      const mappedStatusData: ProjectsCustomizationStatusDto[] = resp.data?.map((item: any) => ({
        id: item.id,
        appName: item.appName,
        hqSiteId: item.hqSiteId,
        appOrigin: item.appOrigin,
        status: item.status,
        customStatus: item.customStatus,
        description: item.description,
        isVisible: item.isVisible
      })) || [];
      
      // Sort by desired order: New, On Hold, Ready to Start, In Progress, Wrap Up, Done
      const statusOrder = ["New", "On Hold", "Ready to Start", "In Progress", "Wrap Up", "Done"];
      const sortedStatusData = mappedStatusData.sort((a, b) => {
        const indexA = statusOrder.indexOf(a.status || "");
        const indexB = statusOrder.indexOf(b.status || "");
        return indexA - indexB;
      });
      
      setStatusManagementData(sortedStatusData);
      originalStatusManagementRef.current = sortedStatusData.map(s => ({ ...s }));
    } catch (err) {
      console.error('Failed to fetch status management data:', err);
      setStatusManagementData([]);
      originalStatusManagementRef.current = [];
    } finally {
      if (showLoader) {
        setLoadingStatusData(false);
      }
    }
  };

  const handleOpenSettings = async () => {
    setIsSettingsOpen(true);
    await Promise.all([fetchProjectSettings(), fetchStatusManagementData()]);
  };

  const saveProjectSettings = async () => {   
    setSavingProjectSettings(true);
    try {            
      var siteId = parseInt(sessionStorage.getItem("siteId")!);
      const settingsObject: any = {};
      projectSettingsData.forEach((prop) => {
        // Persist visibility as the source of truth for client-view column behavior.
        settingsObject[prop.key] = !!prop.visibility;
      });
      
      // Stringify the settings object for the value field
      const valueString = JSON.stringify(settingsObject);
      
      // Prepare update data
      const updateData = {
        id: projectSettingsId || undefined,
        value: valueString,
        property: "UIForProjectSettings"
      };
      
      await createSitesClientApi.tim_UpdateProjectSettings(
        siteId,
        [updateData] as any
      );

      // Refresh the in-memory cache immediately so returning to ClientView
      // doesn't use stale UIForProjectSettings.
      uiProjectSettingsCacheRef.current = { siteId, parsed: settingsObject };

      originalProjectSettingsRef.current = projectSettingsData.map(p => ({ ...p }));
      showSnackbar({message: SnackbarMessage.PROJECT_SETTING_SUCCESS, severity: "success"});
      setIsSettingsOpen(false);
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.FAILED_MESSAGE, severity: "error" });       
    } finally {
      setSavingProjectSettings(false);
    }
  };

  const saveStatusManagementData = async () => {
    setSavingStatusData(true);
    try {
      const siteId = parseInt(sessionStorage.getItem("siteId")!);
      const updateData = {
        hqSiteId: siteId,
        projectsCustomizationSatus: statusManagementData.map(status => ({
          id: status.id,
          customStatus: status.customStatus,
          description: status.description,
          isVisible: status.isVisible
        }))
      };
      
      await createSitesClientApi.tim_ProjectsCustomizationStatusUpdate(updateData as any);
      originalStatusManagementRef.current = statusManagementData.map(s => ({ ...s }));
      showSnackbar({message: SnackbarMessage.PROJECT_SETTING_SUCCESS, severity: "success"});
      return true;
    } catch (error) {
       showSnackbar({ message: SnackbarMessage.FAILED_MESSAGE, severity: "error" });
       return false;
    } finally {
      setSavingStatusData(false);
    }
  };

  const handleCancelSettings = () => {
    if (isSettingsDirty || isStatusDirty) {
      setConfirmExitSettingsOpen(true);
    } else {
      setIsSettingsOpen(false);
    }
  };

  const handleConfirmExitSettings = () => {
    setConfirmExitSettingsOpen(false);
    setIsSettingsOpen(false);
  };

  const handleSaveAllSettings = async () => {
    const shouldSaveProjectSettings = isSettingsDirty;
    const shouldSaveStatusManagement = isStatusDirty;
    const shouldReloadProjects = shouldSaveStatusManagement;

    if (!shouldSaveProjectSettings && !shouldSaveStatusManagement) return;

    try {
      if (shouldSaveProjectSettings) {
        await saveProjectSettings();
      }

      let statusSaved = true;
      if (shouldSaveStatusManagement) {
        statusSaved = await saveStatusManagementData();
      }

      // Close once the save API call(s) completed successfully.
      if (!shouldSaveStatusManagement || statusSaved) {
        setConfirmExitSettingsOpen(false);
        setIsSettingsOpen(false);
      }

      // Only re-render (reload) projects when Status Management changed and saved.
      // Do not block the dialog close on projects reload.
      if (shouldReloadProjects && statusSaved) {
        void loadData(true);
      }
    } catch (error) {
      console.error("Failed to save settings:", error);
    }
  };

  const handleOpenResetProjectSettingsConfirm = () => {
    setConfirmResetProjectSettingsOpen(true);
  };

  const handleCloseResetProjectSettingsConfirm = () => {
    if (resettingProjectSettings) return;
    setConfirmResetProjectSettingsOpen(false);
  };

  const handleConfirmResetProjectSettings = async () => {
    setResettingProjectSettings(true);
    try {
      const siteId = parseInt(sessionStorage.getItem("siteId")!);
      await createSitesClientApi.tim_ResetProjectSettings(siteId);
      // Refresh content without toggling modal loaders to avoid dialog-size flicker.
      await Promise.all([fetchProjectSettings(false), fetchStatusManagementData(false)]);
      void loadData(true);
      setConfirmResetProjectSettingsOpen(false);
      setIsSettingsOpen(false);
      showSnackbar({ message: "Project settings reset successfully.", severity: "success" });
    } catch (error) {
      showSnackbar({ message: SnackbarMessage.FAILED_MESSAGE, severity: "error" });
    } finally {
      setResettingProjectSettings(false);
    }
  };

  const handleSwitchClientView = async () => {
    // If there are unsaved changes (Projects grid or Tasks grid), warn the user before switching.
    if (isDirty || tasksIsDirty) {
      setConfirmSwitchClientViewOpen(true);
      return;
    }
    // Fetch latest project settings before switching view
    if (!isClientViewRef.current) setViewSwitching(true);
    try {
      await fetchProjectSettings();
    } catch (err) {
      setViewSwitching(false);
      throw err;
    }
    // Apply column visibility defaults
    applyGridColumnVisibility(DEFAULT_CLIENT_HIDDEN_COLUMNS);
    viewChangeReloadPendingRef.current = true;
    setIsClientView(true);
    if (!effectiveShowProjects) setActiveView('project');
  };

  const handleConfirmSwitchClientView = async () => {
    setConfirmSwitchClientViewOpen(false);
    if (!isClientViewRef.current) setViewSwitching(true);

    // Revert all unsaved changes so client view reflects the last saved state.
    const originalHidden = originalHiddenColumnsRef.current || [];
    setHiddenColumns([...originalHidden]);
    applyGridColumnVisibility(originalHidden);
    const originalRows = originalRowsRef.current || [];
    setRows(originalRows.map((r) => ({ ...r })));
    try {
      entitiesDirtyByProjectRef.current.clear();
      deliverablesDirtyByProjectRef.current.clear();
    } catch {}
    if (tasksIsDirty) {
      tasksCancelRef.current?.();
      setTasksIsDirty(false);
    }

    await fetchProjectSettings();
    const originalHiddenAfterFetch = originalHiddenColumnsRef.current || [];
    const mergedAfterFetch = Array.from(new Set([...(originalHiddenAfterFetch || []), ...DEFAULT_CLIENT_HIDDEN_COLUMNS]));
    applyGridColumnVisibility(mergedAfterFetch);
    // Determine whether tasks are enabled in fetched settings and, if disabled,
    // force Projects view so the client sees Projects without Tasks.
    try {
      const cached = uiProjectSettingsCacheRef.current?.parsed;
      const tasksEnabled = (cached && typeof cached?.Task !== 'undefined') ? toBoolean(cached?.Task, false) : undefined;
      viewChangeReloadPendingRef.current = true;
      setIsClientView(true);
      if (tasksEnabled === false) setActiveView('project');
    } catch {
      viewChangeReloadPendingRef.current = true;
      setIsClientView(true);
    }
  };

  const handleReturnToInternalView = async () => {
    // Client view is read-only, so reload projects with isClientView=false.
    if (isClientViewRef.current) setViewSwitching(true);
    viewChangeReloadPendingRef.current = true;
    setIsClientView(false);
    // Ref normally syncs on render; update now so this apply targets the internal column set.
    isClientViewRef.current = false;
    applyGridColumnVisibility(hiddenColumnsRef.current || hiddenColumns);
  };

  const handleOpenStatusDetails = () => {
    if (actionRow) {
      const latestRow =
        rowsRef.current.find((r) => String(r.id) === String(actionRow.id)) ||
        actionRow;
      const siteId = parseInt(sessionStorage.getItem("siteId")!);
      if (siteId > 0 && latestRow.projectIdMdm) {
        upsertReadEntry(siteId, latestRow.projectIdMdm, {
          LastStatusNoteReadTime:
            toIsoUtc(latestRow.lastNoteCreated) || new Date().toISOString(),
        });
      }
      repaintProjectRow(latestRow.id);

      setStatusDetailsProject(latestRow);
      setIsStatusDetailsOpen(true);
    }
    closeActions();
  };

  const handleCloseStatusDetails = () => {
    setIsStatusDetailsOpen(false);
    setStatusDetailsProject(null);
  };

  const handleOpenProjectDetails = () => {
    if (actionRow) {
      const latestRow =
        rowsRef.current.find((r) => String(r.id) === String(actionRow.id)) ||
        actionRow;
      setProjectDetailsProject(latestRow);
      setIsProjectDetailsOpen(true);
    }
    closeActions();
  };
    let timBaseUrl = REACT_APP_TIMBASE_URL;

  const handleOpenProjectInTim = () => {
    if (!actionRow?.projectIdTim) {
      closeActions();
      return;
    }
  
    const baseWithoutTrailingSlash = timBaseUrl.trim().replace(/\/+$/, "");
    const timProjectUrl = `${baseWithoutTrailingSlash}/projects/details/${actionRow.id}`;
    window.open(timProjectUrl, "_blank", "noopener,noreferrer");
    closeActions();
  };

  const handleCloseProjectDetails = () => {
    setIsProjectDetailsOpen(false);
    setProjectDetailsProject(null);
  };

  const handleProjectDetailsUpdate = useCallback((args?: {
    projectIdMdm: number;
    projectCustomName?: string;
    engagementManagers?: ProjectPerson[];
  }) => {
    if (!args?.projectIdMdm) return;

    const targetRow = rowsRef.current.find(
      (r) => r.projectIdMdm === args.projectIdMdm,
    );

    setRows((prev) =>
      prev.map((row) =>
        row.projectIdMdm === args.projectIdMdm
          ? {
              ...row,
              projectCustomName:
                typeof args.projectCustomName === "string"
                  ? args.projectCustomName
                  : row.projectCustomName,
              engagementManagers: args.engagementManagers ?? row.engagementManagers,
            }
          : row,
      ),
    );

    // No need to reload projects from the API for engagement manager visibility
    // updates (or project rename); update the cached row and repaint the grid.
    if (targetRow?.id) {
      // Also update the cached dataSource entry so DevExtreme's internal
      // bound row references reflect the new name immediately and repaint
      // the specific row to avoid a full grid refresh.
      try {
        const cached = projectsGridDataSourceRef.current.find((r) => r.id === targetRow.id);
        if (cached) {
          cached.projectCustomName = typeof args.projectCustomName === "string" ? args.projectCustomName : cached.projectCustomName;
          cached.engagementManagers = args.engagementManagers ?? cached.engagementManagers;  
          const cacheEntry = projectsGridRowCacheRef.current.get(targetRow.id);
          if (cacheEntry) cacheEntry.sig = buildProjectRowSig(cached);
        }
      } catch {}

      requestAnimationFrame(() => repaintProjectRow(targetRow.id));
    }
  }, [repaintProjectRow]);

  const handleStatusUpdate = (newStatus: string, notes: string) => {
    // Update the project status in the grid
    if (statusDetailsProject) {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === statusDetailsProject.id
            ? { ...row, status: newStatus as any }
            : row
        )
      );
    }
  };

  const handleRowExpanded = useCallback((e: any) => {
    try {
      const projectId = String(e?.key ?? "");
      if (!projectId) return;
      const row = displayRowsRef.current.find((r) => r.id === projectId);
      if (!row) return;

      const showEntitiesForRow =
        !isClientView ||
        (clientDetailsEnabled.entities && !row.hideallentities);
      const showDeliverablesForRow =
        !isClientView ||
        (clientDetailsEnabled.deliverables && !row.hidealldeliverables);

      if (isClientView && !showEntitiesForRow && !showDeliverablesForRow ) {
        e.cancel = true;
        return;
      }

      const savedTab = detailTabByProjectRef.current[projectId];
      const activeTab =
       savedTab === "deliverables" && showDeliverablesForRow
          ? "deliverables"
          : showEntitiesForRow
          ? "entities"
          : showDeliverablesForRow
          ? "deliverables"
          : "entities";

      if (activeTab === "entities") ensureEntitiesLoaded(projectId);
      else if (activeTab === "deliverables") ensureDeliverablesLoaded(projectId);
    } catch {}
  }, [
    clientDetailsEnabled.deliverables,
    clientDetailsEnabled.entities,
    ensureDeliverablesLoaded,
    ensureEntitiesLoaded,
    isClientView,
  ]);

  const handleRowCollapsing = useCallback((e: any) => {
    try {
      const projectId = String(e?.key ?? "");
      if (!projectId) return;
      const ec = entityAbortControllersRef.current[projectId];
      if (ec) {
        try {
          ec.abort();
        } catch {}
        delete entityAbortControllersRef.current[projectId];
      }
      const dc = deliverableAbortControllersRef.current[projectId];
      if (dc) {
        try {
          dc.abort();
        } catch {}
        delete deliverableAbortControllersRef.current[projectId];
      }
      setEntityLoadingByProject((prev) => {
        if (prev?.[projectId] === false) return prev;
        return { ...prev, [projectId]: false };
      });
      setDeliverableLoadingByProject((prev) => {
        if (prev?.[projectId] === false) return prev;
        return { ...prev, [projectId]: false };
      });
    } catch {}
  }, []);

  const handleSave = async () => {
    // Column chooser selections are persisted immediately to sessionStorage
    // (see handleGridOptionChanged) and are not part of this API save.

    // Build the API payload for changed projects
    setSavingChanges(true);
    let projectVisibilityChanged = false;
    try {
      var siteId = parseInt(sessionStorage.getItem("siteId")!);
      const changedProjects: ProjectsCustomizationDataDto[] = [];
      const changedProjectIds = new Set<string>();
      
      // Track what needs to be reloaded
      const projectsWithEntityChanges = new Set<string>();
      const projectsWithDeliverableChanges = new Set<string>();

      // Compare current rows with original rows
      rows.forEach((currentRow) => {
        const originalRow = originalRowsRef.current.find((r) => r.id === currentRow.id);
        if (!originalRow) return;

        let hasChanges = false;
        let entitiesChanged = false;
        let deliverablesChanged = false;

        // Check if project visibility changed
        if (currentRow.visibility !== originalRow.visibility) {
          hasChanges = true;
          projectVisibilityChanged = true;
        }

        // Check if entity visibility / hide-all state changed
        const currentEntities = currentRow.entities || [];
        const originalEntities = originalRow.entities || [];
        const hideAllEntitiesChanged =
          !!currentRow.hideallentities !== !!originalRow.hideallentities;
        let entityItemVisibilityChanged = false;
        currentEntities.forEach((currentEntity) => {
          const originalEntity = originalEntities.find(
            (e) => String(e.id) === String(currentEntity.id),
          );
          if (originalEntity && currentEntity.visibility !== originalEntity.visibility) {
            entityItemVisibilityChanged = true;
          }
        });

        if (entityItemVisibilityChanged || hideAllEntitiesChanged) {
          entitiesChanged = true;
          hasChanges = true;
        }

        // Check if deliverable visibility / hide-all state changed
        const currentDeliverables = currentRow.deliverables || [];
        const originalDeliverables = originalRow.deliverables || [];
        const hideAllDeliverablesChanged =
          !!currentRow.hidealldeliverables !== !!originalRow.hidealldeliverables;
        let deliverableItemVisibilityChanged = false;
        currentDeliverables.forEach((currentDeliverable) => {
          const originalDeliverable = originalDeliverables.find(
            (d) => String(d.id) === String(currentDeliverable.id),
          );
          if (
            originalDeliverable &&
            currentDeliverable.visibility !== originalDeliverable.visibility
          ) {
            deliverableItemVisibilityChanged = true;
          }
        });

        if (deliverableItemVisibilityChanged || hideAllDeliverablesChanged) {
          deliverablesChanged = true;
          hasChanges = true;
        }

        // If any changes detected, send complete current state for this project
        if (hasChanges) {
          changedProjectIds.add(currentRow.id);
          const hiddenEntityIds = currentEntities
            .filter((e) => !e.visibility)
            .map((e) => Number(e.entityId))
            .filter((id) => Number.isFinite(id));

          const hiddenDeliverableIds = currentDeliverables
            .filter((d) => !d.visibility)
            .map((d) => Number(d.id))
            .filter((id) => Number.isFinite(id));

          const entitiesPayload: Pick<
            ProjectsCustomizationDataDto,
            "hideallentities" | "entitiesToHide"
          > = {};

          if (entitiesChanged) {
            // Save semantics (per requirements):
            // - hideAll=true => send hideallentities=true and force all toggles off
            // - hideAll=false and user is toggling items => send entitiesToHide (even empty if loaded) and OMIT hideallentities
            // - If hideAll was turned off but the subgrid isn't loaded, send an empty list to clear any prior hidden list
            if (currentRow.hideallentities) {
              entitiesPayload.hideallentities = true;
            } else if (currentEntities.length > 0) {
              // Merge locally hidden IDs with orphan IDs (hidden records from unloaded pages).
              // If "Show All" was used (hideAllEntitiesChanged=true), skip orphan IDs so the
              // server clears the entire hidden list, including records the user never loaded.
              const orphanEntityIds = hideAllEntitiesChanged
                ? []
                : (entityOrphanHiddenIdsByProject[currentRow.id] ?? []);
              entitiesPayload.entitiesToHide = Array.from(new Set([...hiddenEntityIds, ...orphanEntityIds]));
              // Intentionally omit hideallentities when false.
              delete (entitiesPayload as any).hideallentities;
            } else if (hideAllEntitiesChanged) {
              // No entities loaded, but hide-all was turned off (e.g., user clicked "Show All").
              // Send an explicit empty list so the backend clears any previously-hidden items.
              entitiesPayload.entitiesToHide = [];
              delete (entitiesPayload as any).hideallentities;
            }
          }

          // Build complete project state
          const projectData: ProjectsCustomizationDataDto = {
            projectIdMDM: currentRow.projectIdMdm,
            projectCustomName: undefined,
            isVisible: currentRow.visibility,

            // Entities
            ...entitiesPayload,

            // Deliverables
            ...(deliverablesChanged
              ? (() => {
                  const payload: Pick<
                    ProjectsCustomizationDataDto,
                    "hidealldeliverables" | "deliverablesToHide"
                  > = {};

                  if (currentRow.hidealldeliverables) {
                    payload.hidealldeliverables = true;
                  } else if (currentDeliverables.length > 0) {
                    const orphanDeliverableIds = hideAllDeliverablesChanged
                      ? []
                      : (deliverableOrphanHiddenIdsByProject[currentRow.id] ?? []);
                    payload.deliverablesToHide = Array.from(new Set([...hiddenDeliverableIds, ...orphanDeliverableIds]));
                    // Intentionally omit hidealldeliverables when false.
                    delete (payload as any).hidealldeliverables;
                  } else if (hideAllDeliverablesChanged) {
                    payload.deliverablesToHide = [];
                    delete (payload as any).hidealldeliverables;
                  }

                  return payload;
                })()
              : {}),

            engamentManagersToHide: undefined,
            notes: undefined,
          };

          changedProjects.push(projectData);

          // Track what to reload
          if (entitiesChanged) {
            projectsWithEntityChanges.add(currentRow.id);
          }
          if (deliverablesChanged) {
            projectsWithDeliverableChanges.add(currentRow.id);
          }
        }
      });

      // Call the API if there are changes
      if (changedProjects.length > 0) {
        const requestData: ProjectsCustomizationDataRequestDto = {
          hqSiteId: siteId,
          projectsCustomizationDatas: changedProjects,
          };

        await createSitesClientApi.tim_ProjectsCustomizationDataUpdate(
          requestData
        );

        // Clear local dirty/bulk-action caches for saved projects
        changedProjectIds.forEach((projectId) => {
          entitiesDirtyByProjectRef.current.delete(projectId);
          deliverablesDirtyByProjectRef.current.delete(projectId);
        });

        // Update the last-saved baseline for the projects we just saved so
        // Save disables again and Cancel reverts correctly.
        if (changedProjectIds.size > 0) {
          const currentById = new Map(rows.map((r) => [String(r.id), r] as const));
          const nextOriginal = (originalRowsRef.current || []).map((orig) => {
            const id = String(orig.id);
            if (!changedProjectIds.has(id)) return orig;
            const cur = currentById.get(id);
            return cur ? (JSON.parse(JSON.stringify(cur)) as ProjectRow) : orig;
          });

          // Edge case: if a changed row was not present in the baseline, append it.
          changedProjectIds.forEach((id) => {
            const exists = nextOriginal.some((r) => String(r.id) === String(id));
            if (exists) return;
            const cur = currentById.get(String(id));
            if (cur) nextOriginal.push(JSON.parse(JSON.stringify(cur)) as ProjectRow);
          });

          originalRowsRef.current = nextOriginal;
        }

        // Reload projects if any project visibility changed
        if (projectVisibilityChanged) {
          await loadData(true);
        }

        // Reload only the subgrid resources that actually changed.
        // (Avoid triggering Entities calls when only Deliverables were updated, and vice versa.)
        projectsWithEntityChanges.forEach((projectId) => {
          ensureEntitiesLoaded(projectId, false, true);
        });
        projectsWithDeliverableChanges.forEach((projectId) => {
          ensureDeliverablesLoaded(projectId, false, true);
        });
      }
    } catch (error) {
      console.error("Error saving changes:", error);
      alert("Failed to save changes. Please try again.");
      return; // Exit early if save failed
    } finally {
      setSavingChanges(false);
    }

    // Update column-chooser baseline after a successful save.
    originalHiddenColumnsRef.current = [...hiddenColumns];

    // Force isDirty to recompute since it compares against refs.
    setDirtyBaselineVersion((v) => v + 1);
  };

  const handleCancel = () => {
    if (!isDirty || savingChanges) {
      return;
    }
    setConfirmCancelGridOpen(true);
  };

  const handleTasksCancelClick = () => {
    if (!tasksIsDirty || tasksIsSaving) {
      return;
    }
    setConfirmCancelTasksOpen(true);
  };

  const handleConfirmCancelGridExit = () => {
    setConfirmCancelGridOpen(false);

    // Revert column chooser changes (local-only)
    const originalHidden = originalHiddenColumnsRef.current || [];
    setHiddenColumns([...originalHidden]);
    applyGridColumnVisibility(originalHidden);

    // Revert local row/entity/deliverable edits from cached state.
    // Do not re-fetch/re-render from the server when nothing was saved.
    const originalRows = originalRowsRef.current || [];
    // Setting the dataSource option alone doesn't reliably repaint already-rendered
    // visibility cells, so track which rows actually changed and repaint just those.
    const dirtyProjectIds = rows
      .filter((r) => {
        const orig = originalRows.find((o) => o.id === r.id);
        return !!orig && orig.visibility !== r.visibility;
      })
      .map((r) => r.id);
    setRows(originalRows.map((r) => ({ ...r })));
    // The [rows] sync effect that rebuilds these refs runs async (after paint), which
    // races with the repaint below - resync synchronously so entities/deliverables/
    // hide-all state is correct before anything re-renders.
    syncVisibilityRefsFromRows(originalRows.map((r) => ({ ...r })));

    // Also clear local dirty trackers so Save/Cancel disable again.
    try {
      entitiesDirtyByProjectRef.current.clear();
      deliverablesDirtyByProjectRef.current.clear();
    } catch {}
    // Row count usually doesn't change on cancel, so the count-based auto-sync effect
    // won't fire on its own; force the grid to pick up the reverted field values.
    requestAnimationFrame(() => {
      const inst = gridRef.current?.instance;
      try { inst?.option("dataSource", projectsGridDataSourceRef.current); } catch {}
      try {
        if (inst && dirtyProjectIds.length) {
          const indexes = dirtyProjectIds
            .map((id) => inst.getRowIndexByKey(id))
            .filter((idx: number) => typeof idx === "number" && idx >= 0);
          if (indexes.length) inst.repaintRows(indexes);
        }
      } catch {}
      // Nested grids' cellRender reads the visibility-map refs above, but their own
      // dataSource memo won't recompute on a same-length revert, so repaint them
      // directly (same scoped pattern the bulk hide/show-all handlers already use).
      try {
        Object.values(entityGridInstancesRef.current).forEach((gi: any) => { try { gi?.repaint(); } catch {} });
        Object.values(deliverableGridInstancesRef.current).forEach((gi: any) => { try { gi?.repaint(); } catch {} });
      } catch {}
      notifyAllProjectDetailsChanged();
    });
  };

  const getInitials = (name: string) => {
    const managersName = typeof name === "string" ? name : "";
    const parts = managersName.trim().split(/\s+/);
    const initials = parts
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || "")
      .join("");
    return initials || managersName[0]?.toUpperCase() || "";
  };

  // Compute display rows based on view mode
  const displayRows = useMemo(
    () => {
      if (!isClientView) return searchedRows;

      // In Client View, do not show projects until global visibility is enabled.
      if (!effectiveShowProjects) {
        return [];
      }

      // If the logged-in user is a true client user, do not allow "preview".
      // Only show rows when projects are enabled globally.
      if (isClientUser) {
        // Client-login payloads may omit `visibility` (defaulted to true), so also honour the server's hidden-project list.
        const hiddenIds = new Set(hiddenProjectIdsForCount);
        return searchedRows.filter(
          (r) => r.visibility && !(typeof r.projectIdMdm === "number" && hiddenIds.has(r.projectIdMdm)),
        );
      }

      // Internal users: allow client-view preview even if projects are hidden globally.
      return searchedRows.filter((r) => r.visibility);
    },
    [isClientView, isClientUser, effectiveShowProjects, searchedRows, hiddenProjectIdsForCount],
  );
  const displayRowsRef = useRef<ProjectRow[]>([]);
  useEffect(() => {
    displayRowsRef.current = displayRows;
  }, [displayRows]);
  const emptyGridRows = useMemo<ProjectRow[]>(() => [], []);
  const gridRows = viewSwitching ? emptyGridRows : displayRows;

  // Cache a stable main-grid datasource so loading entities/deliverables (child grids)
  // doesn't cause the entire parent grid to refresh/reset.
  const projectsGridRowCacheRef = useRef<Map<string, { sig: string; row: ProjectRow }>>(
    new Map(),
  );
  const projectsGridDataSourceRef = useRef<ProjectRow[]>(emptyGridRows);

  const projectsGridDataSource = useMemo(() => {
    if (gridRows === emptyGridRows) {
      projectsGridDataSourceRef.current = emptyGridRows;
      projectsGridRowCacheRef.current.clear();
      return emptyGridRows;
    }

    const cache = projectsGridRowCacheRef.current;

    const next = gridRows.map((r) => {
      const sig = buildProjectRowSig(r);
      const cached = cache.get(r.id);
      if (cached && cached.sig === sig) return cached.row;

      // Strip child collections from the parent grid datasource to avoid refresh
      // when only nested entities/deliverables data changes.
      const viewRow: ProjectRow = {
        ...r,
        entities: undefined,
        deliverables: undefined,
      };
      cache.set(r.id, { sig, row: viewRow });
      return viewRow;
    });

    const prev = projectsGridDataSourceRef.current;
    const same =
      prev.length === next.length && prev.every((item, i) => item === next[i]);
    if (same) return prev;

    projectsGridDataSourceRef.current = next;
    return next;
  }, [emptyGridRows, gridRows]);

  const projectsGridDataSourceLength = projectsGridDataSource.length;
  useEffect(() => {
    const inst = gridRef.current?.instance;
    if (!inst) return;
    try {
      inst.option("dataSource", projectsGridDataSourceRef.current);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsGridDataSourceLength]);
  const clientProjectsUnavailable = isClientView && !effectiveShowProjects;
  // Note: removed full-grid loading overlay during client-view column fetch.
  // Pessimistic-default settings load briefly after ClientView toggles on, so this must
  // only hide the Tasks button when projects are globally hidden for clients.
  // clientDetailsEnabled lags one render behind isClientView (it's set in an effect, briefly
  // pessimistic), which kicked users off Tasks on switch; read the loaded setting directly instead.
  const taskSettingFromState = projectSettingsData.find((p) => p.key === "Task")?.visibility;
  const cachedTaskSetting = uiProjectSettingsCacheRef.current?.parsed?.Task;
  const clientTasksEnabled =
    typeof taskSettingFromState === "boolean"
      ? taskSettingFromState
      : cachedTaskSetting !== undefined
      ? toBoolean(cachedTaskSetting, false)
      : clientDetailsEnabled.tasks;
  const showTasksTopTab = !isClientView || (!!effectiveShowProjects && clientTasksEnabled);
  useEffect(() => {
    if (!showTasksTopTab && activeView === 'task') setActiveView('project');
    // TasksGrid unmounts off the Tasks view, so its selection is gone too.
    if (activeView !== 'task') setTasksSelectionCount(0);
  }, [showTasksTopTab, activeView]);

  // MasterDetail is enabled only when details are available in Client View.

  const handleGridCellPrepared = useCallback((e: any) => {
    if (!isClientView) return;
    if (e?.rowType !== "data") return;

    const command = String(e?.column?.command || "");
    const type = String(e?.column?.type || "");
    const cssClass = String(e?.column?.cssClass || "");
    const isExpandCell =
      command.toLowerCase().includes("expand") ||
      type.toLowerCase().includes("expand") ||
      cssClass.toLowerCase().includes("command-expand");
    if (!isExpandCell) return;

    const row = e?.data as ProjectRow | undefined;
    if (!row) return;
    const showEntitiesForRow =
      clientDetailsEnabled.entities && !row.hideallentities;
    const showDeliverablesForRow =
      clientDetailsEnabled.deliverables && !row.hidealldeliverables;

    const cellElement = (e.cellElement?.get
      ? e.cellElement.get(0)
      : e.cellElement) as HTMLElement | undefined;
    if (!cellElement) return;

    // Avoid DOM churn (replaceChildren/innerHTML) as it can cause flicker with
    // virtual rendering. Prefer a class toggle + CSS.
    const shouldHide = !showEntitiesForRow && !showDeliverablesForRow;
    if (shouldHide) {
      cellElement.classList.add("tp-expand-hidden");
      cellElement.style.pointerEvents = "none";
      cellElement.style.visibility = "hidden";
      return;
    }

    if (cellElement.classList.contains("tp-expand-hidden")) {
      cellElement.classList.remove("tp-expand-hidden");
      cellElement.style.pointerEvents = "";
      cellElement.style.visibility = "";
    }
  }, [isClientView, clientDetailsEnabled.entities, clientDetailsEnabled.deliverables]);

  const handleGridOptionChanged = useCallback((e: any) => {
    if (!hasInitializedColumnVisibilityRef.current) {
      return;
    }
    if (suppressColumnOptionChangedRef.current) {
      return;
    }
    // Client-view column changes are programmatic; never persist them as chooser choices.
    if (isClientViewRef.current) {
      return;
    }
    // Anchored so `columns[N].visibleIndex` (fired on reorder/re-index) isn't mistaken for a show.
    const match = typeof e.fullName === "string" ? e.fullName.match(/^columns\[(\d+)\]\.visible$/) : null;
    if (match) {
      const colIndex = parseInt(match[1], 10);
      const inst = e.component;
      const name =
        colIndex !== undefined
          ? inst.columnOption(colIndex, "name") ||
            inst.columnOption(colIndex, "dataField")
          : undefined;
      const visible = e.value;
      if (!name) return;
        setHiddenColumns((prev) => {
        const colName = String(name);
        const currentlyHidden = prev.includes(colName);
        const willBeHidden = visible === false;
        if (currentlyHidden === willBeHidden) return prev;
        const next = willBeHidden ? [...prev, colName] : prev.filter((x) => x !== colName);
        // Column chooser changes are local-only (no API call): persist immediately
        // to sessionStorage and update the baseline so they never enable Save Changes.
        originalHiddenColumnsRef.current = next;
        // Update synchronously (not via the [hiddenColumns] effect) so a contentReady
        // event firing before React's next render doesn't re-apply a stale hidden set
        // and revert the column the user just hid/shown.
        hiddenColumnsRef.current = next;
        try { localStorage.setItem("projectsGrid_hiddenColumns_v2", JSON.stringify(next)); } catch {}
        return next;
      });
    }
  }, [isClientView]);

  useEffect(() => {
    try {
      const inst = gridRef.current?.instance;
      if (!inst) return;
      if (!isClientView) {
        applyGridColumnVisibility(hiddenColumnsRef.current || []);
      }
    } catch {}
  }, [isClientView, applyGridColumnVisibility]);

  const handleGridContentReady = useCallback((e: any) => {
    try {
      const root = (e?.element?.get ? e.element.get(0) : e?.element) as HTMLElement | undefined;
      const rowsView = root?.querySelector(".dx-datagrid-rowsview") as HTMLElement | null;
      if (rowsView) setProjectsGridRowsViewEl((prev) => (prev === rowsView ? prev : rowsView));
    } catch {}
    // Re-apply column visibility each time content is ready so StateStoring's restored
    // state never leaves columns in a stale hidden/visible state.
    applyGridColumnVisibility(hiddenColumnsRef.current);
    // Do not auto-create a grid state here; StateStoring will load/migrate existing state as needed.
  }, [applyGridColumnVisibility]);

  const engagementRender = useCallback((e: any) => {
    const vals: ProjectPerson[] = e.data.engagementManagers || [];
    const filteredVals = isClientView
      ? vals.filter((m) => m?.visibility !== false)
      : vals;
    const visible = filteredVals.slice(0, 3);
    const extraCount = Math.max(0, filteredVals.length - visible.length);
    return (
      <div
        className="engagement-hover-container"
        onMouseEnter={(event) =>
          openEngagementHover(event.currentTarget, filteredVals)
        }
        onMouseLeave={scheduleCloseEngagementHover}
      >
        <div className="engagement-group">
          {visible.map((v, idx) => (
            <Chip
              key={`${v?.id ?? v?.name ?? idx}-${idx}`}
              label={getInitials(v?.name || "")}
              size="small"
              className="engagement-chip"
            />
          ))}
          {extraCount > 0 && (
            <Chip
              label={`+${extraCount}`}
              size="small"
              className="engagement-extra-chip"
            />
          )}
        </div>
      </div>
    );
  }, [isClientView, openEngagementHover, scheduleCloseEngagementHover]);

  const statusRender = useCallback((e: any) => {
    const s: ProjectRow["status"] = e.data.status;
    return (
      <Chip
        label={s}
        size="small"
        className={`chip status ${String(s)
          .toLowerCase()
          .replace(/\s+/g, "-")}`}
        variant="outlined"
      />
    );
  }, []);

    const projectCellRender = useCallback((e: any) => {
    const text = getProjectDisplayName(e.data as ProjectRow) || "";
    return (
      <span className="tp-project-text" title={text}>
        {text}
      </span>
    );
  }, []);

  // Common visibility cell renderer factory for both Entities and Deliverables.
  // Cached per (kind, projectId) so `renderDetail` re-invocations reuse the SAME
  // function reference, keeping the nested grid's memo stable across toggles.
  const subViewCellRenderCacheRef = useRef<Map<string, { toggleFn: any; render: (cell: any) => React.ReactNode }>>(new Map());
  const subViewCellRender = useCallback((
    kind: "entities" | "deliverables",
    projectId: string,
    toggleFn: (projectId: string, id: number | string, checked: boolean) => void,
  ) => {
    const cacheKey = `${kind}::${projectId}`;
    const cached = subViewCellRenderCacheRef.current.get(cacheKey);
    if (cached && cached.toggleFn === toggleFn) return cached.render;

    const visMapRef = kind === "entities" ? entityVisibilityMapRef : deliverableVisibilityMapRef;
    const render = (cell: any) => {
      const id = cell.data?.id;
      const map = visMapRef.current[projectId] || {};
      const visible = typeof map[String(id)] === "boolean" ? map[String(id)] : !!cell.data?.visibility;
      return (
        <Stack direction="row" sx={{ width: "100%", alignItems: "center", justifyContent: "center" }}>
          <IconButton
            onClick={() => toggleFn(projectId, id, !visible)}
            aria-label="visibility toggle"
          >
            {visible ? (
              <ToggleOnIcon sx={{ fontSize: 28, color: "#00a3e0", ml: "2px" }} />
            ) : (
              <ToggleOffIcon sx={{ fontSize: 28, color: "#666666", ml: "2px" }} />
            )}
          </IconButton>
        </Stack>
      );
    };
    subViewCellRenderCacheRef.current.set(cacheKey, { toggleFn, render });
    return render;
  }, []);


  const visibilityRender = useCallback((e: any) => {
    const row: ProjectRow = e.data;
    const visible = typeof projectVisibilityRef.current[row.id] === 'boolean' ? projectVisibilityRef.current[row.id] : !!row.visibility;
    return (
      <Stack
        direction="row"
        sx={{ alignItems: "center", justifyContent: "center" }}
      >
        <IconButton
          disableRipple
          disableFocusRipple
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            handleRowVisibilityToggle(row, !visible);
          }}
          aria-label="visibility toggle"
        >
          {visible ? (
            <ToggleOnIcon sx={{ fontSize: 28, color: '#00a3e0' }} />
          ) : (
            <ToggleOffIcon sx={{ fontSize: 28, color: '#666666' }} />
          )}
        </IconButton>
      </Stack>
    );
  }, [handleRowVisibilityToggle]);

  const actionsRender = useCallback((e: any) => {
    const row: ProjectRow = e.data;
    return (
      <IconButton aria-label="actions" onClick={(ev) => openActions(ev, row)}>
        <MoreVertIcon />
      </IconButton>
    );
  }, [openActions]);

  const engagementManagersCellValue = useCallback(
    (data: ProjectRow) =>
      (data.engagementManagers || [])
        .map((m: any) => `${m?.name || ""} ${m?.email || ""}`.trim())
        .filter(Boolean)
        .join("; "),
    [],
  );

  const unreadCellRender = useCallback((cell: any) => {
    const row = cell.data as ProjectRow;
    const siteId = parseInt(sessionStorage.getItem("siteId") || "0");
    const projectIdMdm = row?.projectIdMdm;
    if (!siteId || !projectIdMdm) {
      return (
        <span className="unread-icons">
          <span className="unread-message-icon" aria-label="Messages">
            <ChatOutlinedIcon sx={{ fontSize: 18, color: "#666666" }} />
          </span>
          <span className="unread-note-icon" aria-label="Notes">
            <ListAltOutlinedIcon sx={{ fontSize: 18, color: "#666666" }} />
          </span>
        </span>
      );
    }

    const currentUserEmail = getCurrentUserEmail();
    const readEntry = getReadEntry(siteId, projectIdMdm);

    const canShowStatusDetails = !isClientView || clientStatusDetailsEnabled;

    const lastMessageCreated = row?.lastMessageCreated;
    const lastMessageCreatedBy = normalizeUserId(row?.lastMessageCreatedBy);
    const lastNoteCreated = row?.lastNoteCreated;
    const lastNoteCreatedBy = normalizeUserId(row?.lastNoteCreatedBy);

    const lastMessageReadTime = readEntry?.LastMessageReadTime;
    const lastNoteReadTime = readEntry?.LastStatusNoteReadTime;

    const isUnreadMessage = (() => {
      if (!lastMessageCreated) return false;
      const createdByDifferent = lastMessageCreatedBy
        ? lastMessageCreatedBy !== currentUserEmail
        : true;
      if (!createdByDifferent) return false;
      if (!lastMessageReadTime) return true;
      return isUnreadSinceLastRead(lastMessageCreated, lastMessageReadTime);
    })();

    const isUnreadNote = (() => {
      if (!canShowStatusDetails) return false;
      if (!lastNoteCreated) return false;
      const createdByDifferent = lastNoteCreatedBy
        ? lastNoteCreatedBy !== currentUserEmail
        : true;
      if (!createdByDifferent) return false;
      if (!lastNoteReadTime) return true;
      return isUnreadSinceLastRead(lastNoteCreated, lastNoteReadTime);
    })();

    return (
      <span className="unread-icons">
        <Tooltip title={isUnreadMessage ? "You have unread messages" : "Messages"}>
          <span className="unread-message-icon" aria-label="Messages">
            <ChatOutlinedIcon sx={{ fontSize: 18, color: "#666666" }} />
            {isUnreadMessage && <span className="unread-dot" />}
          </span>
        </Tooltip>
        <Tooltip title={isUnreadNote ? "You have unread notes" : "Notes"}>
          <span className="unread-note-icon" aria-label="Notes">
            <ListAltOutlinedIcon sx={{ fontSize: 18, color: "#666666" }} />
            {isUnreadNote && <span className="unread-dot" />}
          </span>
        </Tooltip>
      </span>
    );
  }, [clientStatusDetailsEnabled, getCurrentUserEmail, getReadEntry, isClientView, isUnreadSinceLastRead]);

  const handleDetailTabChange = useCallback((projectId: string, tab: "entities" | "deliverables") => {
    setDetailTabByProject((prev) => (prev[projectId] === tab ? prev : { ...prev, [projectId]: tab }));
    if (tab === "entities") ensureEntitiesLoaded(projectId);
    else ensureDeliverablesLoaded(projectId);
    // Deferred so detailTabByProjectRef (updated from the state above) is current by
    // the time the panel re-renders. This re-renders only that panel via React state,
    // no DevExtreme repaint of the master-detail row involved.
    requestAnimationFrame(() => notifyProjectDetailChanged(projectId));
  }, [ensureEntitiesLoaded, ensureDeliverablesLoaded, notifyProjectDetailChanged]);

  const renderDetail = useCallback((e: any) => {
    const projectId = (e.data as ProjectRow).id;
    return (
      <ProjectDetailPanel
        key={projectId}
        projectId={projectId}
        isClientViewRef={isClientViewRef}
        clientDetailsEnabledRef={clientDetailsEnabledRef}
        rowsRef={rowsRef}
        detailTabByProjectRef={detailTabByProjectRef}
        entityLoadingByProjectRef={entityLoadingByProjectRef}
        deliverableLoadingByProjectRef={deliverableLoadingByProjectRef}
        entityTotalByProjectRef={entityTotalByProjectRef}
        deliverableTotalByProjectRef={deliverableTotalByProjectRef}
        entityLastFetchCountByProjectRef={entityLastFetchCountByProjectRef}
        deliverableLastFetchCountByProjectRef={deliverableLastFetchCountByProjectRef}
        entityLoadingMoreByProjectRef={entityLoadingMoreByProjectRef}
        deliverableLoadingMoreByProjectRef={deliverableLoadingMoreByProjectRef}
        subViewCellRender={subViewCellRender}
        handleEntityVisibilityToggle={handleEntityVisibilityToggle}
        handleDeliverableVisibilityToggle={handleDeliverableVisibilityToggle}
        handleBulkEntityVisibilityToggle={handleBulkEntityVisibilityToggle}
        handleBulkDeliverableVisibilityToggle={handleBulkDeliverableVisibilityToggle}
        ensureEntitiesLoaded={ensureEntitiesLoaded}
        ensureDeliverablesLoaded={ensureDeliverablesLoaded}
        registerEntityGridInstance={registerEntityGridInstance}
        registerDeliverableGridInstance={registerDeliverableGridInstance}
        onTabChange={handleDetailTabChange}
        registerUpdater={registerProjectDetailUpdater}
      />
    );
  }, [
    ensureDeliverablesLoaded,
    ensureEntitiesLoaded,
    handleBulkDeliverableVisibilityToggle,
    handleBulkEntityVisibilityToggle,
    handleDeliverableVisibilityToggle,
    handleEntityVisibilityToggle,
    handleDetailTabChange,
    subViewCellRender,
    registerEntityGridInstance,
    registerDeliverableGridInstance,
    registerProjectDetailUpdater,
  ]);

  
  
  const projectsDataGrid = useMemo(() => {
    return (
      <div className="nested-grid-card">
      <DataGrid
        ref={gridRef}
        allowColumnResizing={true}
        allowColumnReordering={false}
        
        dataSource={projectsGridDataSourceRef.current}
        repaintChangesOnly={true}
        showBorders={true}
        showColumnLines={false}
        showRowLines={false}
        onInitialized={(e: any) => attachGridAutoHeight(e.component, () => calculateGridHeight(timGridOffset(260)))}
        
        remoteOperations={false}
        noDataText={
          (pending || loadingVisibility || savingVisibility || viewSwitching) && projectsGridDataSourceLength === 0
            ? ""
            : clientProjectsUnavailable
            ? ""
            : "No Data"
        }
        rowAlternationEnabled={false}
        keyExpr="id"
        onRowExpanding={handleRowExpanded}
        onRowCollapsing={handleRowCollapsing}
        onCellPrepared={handleGridCellPrepared}
        onContentReady={handleGridContentReady}
        onOptionChanged={handleGridOptionChanged}
      >
        <StateStoring
          enabled={true}
          type="custom"
          storageKey="projectsGrid"
          customLoad={() => {
              try {
                // Only trust state saved under the current schema version; anything held
              // over from a previous version (in Recoil memory or an older sessionStorage
              // payload) is discarded instead of being silently misapplied.
                const current = projectsGridStateRef.current;
                if (current?.__v === GRID_STATE_SCHEMA_VERSION) {
                  return Promise.resolve(JSON.parse(JSON.stringify(current.data)));
                }
                // Prefer localStorage for persisted grid state, migrate from sessionStorage if present
                const localRaw = localStorage.getItem('projectsGridState_v2');
                const sessionRaw = sessionStorage.getItem('projectsGridState_v2');
                const raw = localRaw ?? sessionRaw ?? null;
                if (raw) {
                  const parsed = JSON.parse(raw);
                  if (parsed?.__v === GRID_STATE_SCHEMA_VERSION) {
                    // migrate session -> local when needed
                    if (!localRaw && sessionRaw) {
                      try { localStorage.setItem('projectsGridState_v2', JSON.stringify(parsed)); } catch {}
                    }
                    // Merge persisted hidden-columns into the loaded grid state so the
                    // column chooser accurately reflects user choices immediately.
                    try {
                      const hiddenRaw = localStorage.getItem('projectsGrid_hiddenColumns_v2') ?? sessionStorage.getItem('projectsGrid_hiddenColumns_v2') ?? null;
                      const hiddenList: string[] = hiddenRaw ? JSON.parse(hiddenRaw) : [];
                      if (parsed.data && Array.isArray(parsed.data.columns) && Array.isArray(hiddenList)) {
                        const hiddenSet = new Set(hiddenList);
                        parsed.data.columns = parsed.data.columns.map((c: any) => {
                          const name = c?.name || c?.dataField || String(c?.caption || '');
                          if (!name) return c;
                          return { ...c, visible: !hiddenSet.has(name) };
                        });
                        let idx = 0;
                        for (const col of parsed.data.columns) {
                          if (col.visible) {
                            col.visibleIndex = idx++;
                          }
                        }
                      }
                    } catch {}
                    // Return null so DevExtreme uses the JSX column definitions
                    // and we apply persisted visibility/order against the actual
                    // instance columns later (avoid re-creating/appending columns).
                    return Promise.resolve(null);
                  }
                }
              } catch {}
              return Promise.resolve(null);
            }}
            customSave={(state: any) => {
                if (isClientViewRef.current) return Promise.resolve();
                try {
                  // persist to recoil (in-memory) and sessionStorage so column chooser
                  // selections survive reloads; this is UI-only and never marks the
                  // app dirty / enables Save Changes.
                  const wrapped = { __v: GRID_STATE_SCHEMA_VERSION, data: state };
                  setProjectsGridState(wrapped);
                  try { localStorage.setItem('projectsGridState_v2', JSON.stringify(wrapped)); } catch {}
                } catch {};
                return Promise.resolve();
              }}
        />
        <LoadPanel enabled={false} />
        <MasterDetail enabled={!isClientView || clientDetailsEnabled.entities || clientDetailsEnabled.deliverables} render={renderDetail} />
        <Selection mode="none" />
        <Paging enabled={false} />        
        <Scrolling mode="standard" columnRenderingMode="virtual" />
        <FilterRow visible={true} />
        {/* No visibleIndex/visible props: devextreme-react rolls controlled column props back,
            which breaks chooser show/hide and order. Order = declaration order; see applyGridColumnVisibility. */}
        <Column dataField="clientId" name="clientId" caption="Entity ID" minWidth={90} alignment="left" allowHiding={true} showInColumnChooser={!isClientView} />
        <Column dataField="client" name="client" caption="Entity" minWidth={160} alignment="left" allowHiding={true} />
        <Column
          dataField="project"
          name="project"
          caption="Project"
          minWidth={140}
          width={140}
          alignment="left"
          allowHiding={true}
          cssClass="tp-project-cell"
          calculateCellValue={(r: ProjectRow) => getProjectDisplayName(r)}
          cellRender={projectCellRender}
        />
        <Column dataField="consent7216" name="consent7216" caption="7216 Consent" minWidth={105} alignment="left" allowHiding={true} />
        <Column dataField="taxYear" name="taxYear" caption="Tax Year" minWidth={90} alignment="left" allowHiding={true} cellRender={(e: any) => e.value || ""} />
        <Column dataField="engagementManagers" name="engagementManagers" caption="Engagement Managers" minWidth={180} alignment="left" allowHiding={true} allowFiltering={true} calculateCellValue={engagementManagersCellValue} cellRender={engagementRender} />
        <Column dataField="status" name="status" caption="Status" minWidth={105} alignment="left" allowHiding={true} cellRender={statusRender} />
        <Column name="Communication" caption="Communication" minWidth={130} alignment="center" allowHiding={true} allowFiltering={false} cellRender={unreadCellRender} />
        <Column dataField="visibility" name="visibility" caption="Visibility" showInColumnChooser={!isClientView} minWidth={90} alignment="center" allowHiding={true} allowFiltering={false} cellRender={visibilityRender} />
        <Column name="actions" caption="Actions" minWidth={64} alignment="center" allowHiding={false} allowFiltering={false} cssClass="tp-actions-cell" cellRender={actionsRender} />
      </DataGrid>
      </div>
    );
  }, [pending, loadingVisibility, savingVisibility, viewSwitching, projectsGridDataSourceLength, clientProjectsUnavailable, handleRowExpanded, handleRowCollapsing, handleGridCellPrepared, handleGridContentReady, handleGridOptionChanged, isClientView, clientDetailsEnabled.entities, clientDetailsEnabled.deliverables, renderDetail, projectCellRender, engagementManagersCellValue, engagementRender, statusRender, unreadCellRender, visibilityRender, actionsRender, setProjectsGridState]);

  // Do not render any UI until we know whether the logged-in user is a client user.
  if (!hasDeterminedUserType) {
    return null;
  }

  return (
    <>
    <div className={`projects-page ${TIM_COMPACT_CLASS}`}>
      {/* Top toolbar: show for both Projects and Tasks. For Tasks, include same buttons except "Switch to Client View" */}
      <div className="projects-toolbar">
        <div className="toolbar-left">
          {!isClientView && (
            <div className="toggle">
              <Button
                size="large"
                color="inherit"
                sx={buttonSizing}
                startIcon={draftShowProjects ? <ToggleOnIcon sx={{ fontSize: "35px", color: '#00a3e0' }} /> : <ToggleOffIcon sx={{ fontSize: "35px", color: "#666666" }} />}
                onClick={() => handleToggleVisibilityBanner(!draftShowProjects)}
                disabled={loadingVisibility}
              ></Button>
              <span><b className="toggle-label">Show Projects</b></span>
              {loadingVisibility && (
                <CircularProgress size={14} sx={{ ml: 1, color: "#00a3e0" }} />
              )}
            </div>
          )}
        </div>
        <div className="toolbar-right">
          {!isClientView ? (
            <>
              <Button
                variant="outlined"
                size="small"
                sx={{ fontSize: "14px" }}
                startIcon={<SettingsIcon sx={{ color: "#515356" }} />}
                onClick={handleOpenSettings}
                className="btn btn-secondary"
              >
                Project Settings
              </Button>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon sx={{ color: "#00bcd4" }} />}
                onClick={handleSwitchClientView}
                className="btn btn-client-view"
              >
                Switch to Client View
              </Button>
            </>
          ) : !isClientUser ? (
            <Button
              variant="outlined"
              startIcon={<VisibilityIcon sx={{ color: "#00a3e0" }} />}
              onClick={handleReturnToInternalView}
              className="btn btn-return-view"
            >
              Return to Internal View
            </Button>
          ) : null}
        </div>
      </div>
      {!loadingVisibility && !effectiveShowProjects && !isClientView && (
        <Alert className="projects-banner" severity="info">
          <b>Projects are currently hidden.</b> <br /> No projects are visible
          to the client yet. This is a new tax year (or site). When ready,
          toggle Show Projects to import and display all project data.
          <br />
          Note: Enabling this will make projects and related data visible to
          clients.
        </Alert>
      )}

      {showTempBanner && !isClientView && (
        <Alert
          className="projects-banner"
          severity="success"
          onClose={() => setShowTempBanner(false)}
        >
          <b> Projects are now visible to clients.</b> <br></br> Projects,
          associated entities, deliverables and engagement manager details are now
          visible to clients. <br></br>
          To adjust what clients can see, manage visibily configurations through
          Project Settings or Project Details.
        </Alert>
      )}

      {(
        <div className="projects-toggle-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="view-toggle">
            <button
              className={`btn-toggle ${activeView === 'project' ? 'active' : ''}`}
              onClick={() => handleTopLevelViewChange('project')}
              aria-pressed={activeView === 'project'}
            >
              Projects
            </button>
            {showTasksTopTab && (
              <button
                className={`btn-toggle ${activeView === 'task' ? 'active' : ''}`}
                onClick={() => handleTopLevelViewChange('task')}
                aria-pressed={activeView === 'task'}
              >
                Tasks
              </button>
            )}
            {activeView === 'task' && showTasksTopTab && (
              <span className="tasks-selection-top-count" style={{ marginLeft: 8, color: '#00a3e0', fontWeight: 600 }}>{tasksSelectionCount} selected</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {activeView === 'project' && (
              <>
                {!isClientView && SHOW_COLUMN_CHOOSER_BUTTON && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ViewColumnIcon sx={{ color: "#515356", fontSize: "small" }} />}
                    onClick={() => gridRef.current?.instance?.showColumnChooser()}
                    className="btn btn-column-chooser"
                  ></Button>
                )}
                {!clientProjectsUnavailable && (
                <TextField
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search..."
                  className="search-input"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#757575", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                />
                )}
                {!isClientView && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSave}
                    disabled={!isDirty || savingChanges}
                    sx={{ textTransform: 'none', fontSize: '14px' }}
                  >
                    {savingChanges ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
              </>
            )}

            {activeView === 'task' && (
              <>
                <Button variant="outlined" size="small" startIcon={<EditIcon sx={{ color: '#515356' }} />} endIcon={<KeyboardArrowDownIcon sx={{ color: '#515356' }} />} className="btn-grey" onClick={() => tasksToolbarControls?.openEditTask?.()}>Edit</Button>
                {!isClientView && (
                <Tooltip title="Columns" placement="bottom" arrow>
                  <span>
                    <Button variant="outlined" startIcon={<ViewColumnIcon sx={{ color: '#515356', fontSize: 'small' }} />} onClick={() => tasksToolbarControls?.showColumnChooser?.()} className="btn-column-chooser" aria-label="Columns" />
                  </span>
                </Tooltip>
                )}
                <Tooltip title="Filter" placement="bottom" arrow>
                  <span>
                    {(() => {
                      const f = tasksToolbarControls?.getFilters?.() || {};
                      const hasModalFilters = ['projectEntity', 'entity', 'project', 'stage', 'task'].some((k) => {
                        const v = (f as any)[k];
                        if (Array.isArray(v)) return v.length > 0;
                        return v !== undefined && v !== null && String(v).trim() !== '';
                      });
                      return (
                        <Button
                          variant="outlined"
                          startIcon={<FilterListIcon sx={{ ...(hasModalFilters ? { color: 'primary.main' } : { color: '#515356' }), fontSize: 'small' }} />}
                          onClick={() => tasksToolbarControls?.openFilter?.()}
                          className="btn-column-chooser"
                          aria-label="Filter"
                          sx={hasModalFilters ? { borderColor: 'primary.main', color: 'primary.main' } : {}}
                        />
                      );
                    })()}
                  </span>
                </Tooltip>
                <Tooltip title="Reset Filters" placement="bottom" arrow>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={<FilterAltOffIcon sx={{ color: '#515356', fontSize: 'small' }} />}
                      onClick={() => tasksToolbarControls?.resetFilters?.()}
                      className={`btn-column-chooser`}
                      aria-label="Reset "
                      disabled={!(Object.values(tasksToolbarControls?.getFilters?.() || {}).some((v: any) => v !== undefined && String(v).trim() !== ''))}
                    />
                  </span>
                </Tooltip>
                <TextField
                  size="small"
                  value={tasksSearch}
                  onChange={(e) => { setTasksSearch(e.target.value); tasksToolbarControls?.setSearch?.(e.target.value); }}
                  placeholder="Search..."
                  className="search-input"
                  InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: '#757575' }} /></InputAdornment>) }}
                />
                {!isClientView && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => handleTasksSaveClick()}
                    disabled={!tasksIsDirty || tasksIsSaving}
                    sx={{ textTransform: 'none', fontSize: '14px' }}
                  >
                    {tasksIsSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <div className={`projects-content projects-grid ${(pending && page === 1) ? 'loading' : ''}`}>
        <div className="projects-grid-wrapper">
          {/* Kept mounted (CSS-hidden) instead of conditionally rendered, so switching to
              Tasks and back never destroys/recreates the grid or any expanded project's
              master-detail subgrids. */}
          <div style={{ display: activeView === 'project' ? undefined : 'none' }}>{projectsDataGrid}</div>
          {activeView === 'task' && showTasksTopTab && <TasksGrid isClientView={isClientView} isSwitchingView={viewSwitching} onCountChange={setTasksCount} onSwitchToClientView={handleSwitchClientView} onSave={handleSave} isDirty={isDirty} savingChanges={savingChanges} onDirtyChange={setTasksIsDirty} onSavingChange={setTasksIsSaving} onCancelRequest={() => setTasksIsDirty(false)} cancelRef={tasksCancelRef} onSelectionCountChange={setTasksSelectionCount} registerToolbarControls={(c: any) => { setTasksToolbarControls(c); try { setTasksSearch(c.getSearch?.() || ""); } catch {} }} />}

          

          {((pending && rows.length === 0) || viewSwitching) && activeView === 'project' ? (
            <div className="projects-grid-inline-overlay">
              <div className="projects-grid-loading-card">
                <CircularProgress size={18} sx={{ color: "#00a3e0", mr: 1 }} />
                <span>Loading projects...</span>
              </div>
            </div>
          ) : null}

          {activeView === 'project' && clientProjectsUnavailable ? (
            <div className="projects-grid-empty-overlay">
              <div className="projects-unavailable">
                <div className="projects-unavailable-title">Projects are not yet available.</div>
                <div className="projects-unavailable-subtitle">Your engagement team has not made project data visible at this time.</div>
                <div className="projects-unavailable-subtitle">When access is enabled, your active projects will appear here.</div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
        {activeView === 'project' && !clientProjectsUnavailable && !viewSwitching && !(pending && page === 1) && (
          <>
            <div className="projects-pagination">
              <div className="pagination-right">
                <Button
                  variant="text"
                  className="load-more-link"
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={
                    pending ||
                    rows.length === 0 ||
                    lastProjectsFetchCount < PROJECTS_PAGE_SIZE ||
                    (totalCount > 0 && rows.length >= totalCount)
                  }
                >
                  {pending && page > 1
                    ? "Loading..."
                    : "Load the next {} records".replace("{}", PROJECTS_PAGE_SIZE.toString())}
                </Button>
              </div>
            </div>
            <div className="loaded-projects-bottom">
              {(() => {
                const effectiveHiddenSet = new Set<number>(hiddenProjectIdsForCount);
                if (isClientView) {
                  // Include unsaved local toggles in client-view count label.
                  rows.forEach((row) => {
                    const id = row.projectIdMdm;
                    if (typeof id !== "number") return;
                    if (row.visibility === false) {
                      effectiveHiddenSet.add(id);
                    } else {
                      effectiveHiddenSet.delete(id);
                    }
                  });
                }

                const loadedCountForLabel = isClientView
                  ? rows.reduce((count, row) => {
                      const id = row.projectIdMdm;
                      const isHidden =
                        typeof id === "number"
                          ? effectiveHiddenSet.has(id)
                          : row.visibility === false;
                      return isHidden ? count : count + 1;
                    }, 0)
                  : rows.length;

                const displayTotalBase = totalCount > 0 ? totalCount : rows.length;
                const displayTotal = isClientView
                  ? Math.max(0, displayTotalBase - effectiveHiddenSet.size)
                  : displayTotalBase;

                const displayStart = loadedCountForLabel > 0 ? 1 : 0;
                const displayEnd = Math.min(loadedCountForLabel, displayTotal);
                return (
                  <>
                    Loaded projects: <b>{displayStart}-{displayEnd}</b> of <b>{displayTotal}</b> matching records.
                  </>
                );
              })()}
            </div>
          </>
        )}

      <div className={`projects-footer${isInIframe ? " projects-footer-iframe" : ""}`}>
            {!isClientView && (
              <>
                <Button variant="outlined" onClick={activeView === 'project' ? handleCancel : handleTasksCancelClick} disabled={activeView === 'project' ? savingChanges : tasksIsSaving}>
                  Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={activeView === 'project' ? handleSave : (activeView === 'task' ? handleTasksSaveClick : undefined)}
                    disabled={activeView === 'project' ? (!isDirty || savingChanges) : (!tasksIsDirty || tasksIsSaving)}
                >
                  {(activeView === 'project' ? savingChanges : tasksIsSaving) ? "Saving..." : "Save Changes"}
                </Button>
              </>
            )}
      </div>

      {/* Actions menu */}
      <Popper
        open={!!actionsMenuPosition && !!actionRow}
        anchorEl={actionsMenuAnchor}
        placement="bottom-start"
        modifiers={[
          { name: "offset", options: { offset: [0, 6] } },
          { name: "preventOverflow", options: { padding: 8 } },
          { name: "flip", options: { padding: 8 } },
        ]}
        sx={{ zIndex: 1400 }}
      >
        <ClickAwayListener onClickAway={closeActions}>
          <Paper elevation={6}>
            {(!isClientView || clientStatusDetailsEnabled) && (
              <MenuItem
                sx={{ padding: 1.5 }}
                disabled={!actionRow}
                onClick={handleOpenStatusDetails}
              >
                <span className="action-icon-wrapper">
                  <AssignmentIcon sx={{ fontSize: 20, color: "#666666" }} />
                </span>
                Status Details
              </MenuItem>
            )}
            <MenuItem
               sx={{ padding: 1.5}}
              disabled={!actionRow}
              onClick={handleOpenMessages}
            >
              <span className="action-icon-wrapper">
                <MessageIcon sx={{ fontSize: 20, color: "#666666" }} />
              </span>
              Messages
            </MenuItem>
            {!isClientView && (
              <MenuItem
                 sx={{ padding: 1.5}}
                disabled={!actionRow}
                onClick={handleOpenProjectDetails}
              >
                <SettingsIcon sx={{ mr: 1, fontSize: 20, color: "#666666" }} />
                Project Details
              </MenuItem>
            )}
            {!isClientView && (
              <MenuItem
                 sx={{ padding: 1.5 }}
                disabled={!actionRow}
                onClick={handleOpenProjectInTim}
              >
                <OpenInNewIcon sx={{ mr: 1, fontSize: 20, color: "#666666" }} />
                View Project in TIM
              </MenuItem>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* Settings modal */}
      <Dialog 
        open={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)}
        maxWidth="md"
        fullWidth
        className="project-settings-dialog"
      >
        <DialogTitle>
          Project Settings
          <IconButton
            aria-label="close"
            onClick={() => setIsSettingsOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <span className="close-icon">✕</span>
          </IconButton>
        </DialogTitle>
        <DialogContent className="project-settings-dialog-content">
          <Tabs 
            value={settingsTab} 
            onChange={(_, v) => setSettingsTab(v)}
            className="project-settings-tabs"
          >
            <Tab
              value="project"
              label="Project Management"
              sx={{
                textTransform: "none",
                fontWeight: (theme) => theme.typography.fontWeightBold,
              }}
            />
            <Tab
              value="status"
              label="Status Management"
              sx={{
                textTransform: "none",
                fontWeight: (theme) => theme.typography.fontWeightBold,
              }}
            />
          </Tabs>
          
          {settingsTab === "project" && (
            <div className="settings-content settings-content-scrollable">
              <div className="settings-description">
                Manage your project configurations across all projects. Control visibility for specific properties to determine what information is shared with clients.
              </div>
              
              <div className="project-settings-table">
                <div className="settings-table-header">
                  <div>Property</div>
                  <div>Notes</div>
                  <div className="visibility-col">Visibility</div>
                </div>
                
                {loadingProjectSettings ? (
                  <div className="settings-loading">
                    <CircularProgress size={18} sx={{ color: "#00a3e0" }} />
                    <span>Loading project settings...</span>
                  </div>
                ) : projectSettingsData.length === 0 ? (
                  <div className="settings-no-data">
                    No project settings available.
                  </div>
                ) : projectSettingsData.map((prop, idx) => (
                  <div 
                    key={prop.key}
                    className="settings-table-row"
                  >
                    <div>{prop.label}</div>
                    <div>
                      <TextField
                        size="small"
                        fullWidth
                        value={prop.key === 'EntityId' || prop.key === 'Consent7216' ? 'Hidden by default' : prop.note}
                        onChange={(e) => {
                          setProjectSettingsData(prev => 
                            prev.map(p => 
                              p.key === prop.key 
                                ? { ...p, note: e.target.value } 
                                : p
                            )
                          );
                        }}
                       // placeholder="Add notes..."
                        variant="standard"
                        disabled={true}
                        InputProps={{
                          disableUnderline: true,
                        }}
                        sx={{ 
                          '& .MuiInputBase-input': { 
                            padding: '8px 0px !important'
                           },
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: 'inherit',
                            color: 'rgba(0, 0, 0, 0.67)'
                          }
                        }}
                      />
                    </div>
                    <div className="visibility-cell">
                      {(prop.key === 'EntityId' || prop.key === 'Consent7216') ? (
                        <Tooltip title="This column visibility cannot be changed" arrow placement="left">
                          <span className="locked-toggle">
                            <Button
                              size="large"
                              startIcon={prop.visibility ? <ToggleOnIcon sx={{ fontSize: '36px !important', color: '#bbb' }} /> : <ToggleOffIcon sx={{ fontSize: '36px !important', color: '#bbb' }} />}
                              disabled
                              sx={{ minWidth: 'auto', padding: 0, pointerEvents: 'none' }}
                            />
                          </span>
                        </Tooltip>
                      ) : (
                        <Button
                          size="large"
                          startIcon={prop.visibility ? <ToggleOnIcon sx={{ fontSize: '36px !important', color: '#00a3e0' }} /> : <ToggleOffIcon sx={{ fontSize: '36px !important', color: '#666666' }} />}
                          onClick={() => {
                            setProjectSettingsData(prev =>
                              prev.map(p =>
                                p.key === prop.key
                                  ? { ...p, visibility: !p.visibility }
                                  : p
                              )
                            );
                          }}
                          sx={{ minWidth: 'auto', padding: 0 }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {settingsTab === "status" && (
            <div className="settings-content settings-content-scrollable">
              <div className="settings-description">
                Manage project statuses across all projects. Create custom names and control which statuses are visible to your clients.
              </div>
              
              <div className="project-settings-table">
                <div className="settings-table-header status-table-header">
                  <div>Status</div>
                  <div>Custom Name</div>
                  <div>Description</div>
                  <div className="visibility-col">Visibility</div>
                </div>
                
                {loadingStatusData ? (
                  <div className="settings-loading">
                    <CircularProgress size={18} sx={{ color: "#00a3e0" }} />
                    <span>Loading status settings...</span>
                  </div>
                ) : statusManagementData.length === 0 ? (
                  <div className="settings-no-data">
                    No status data available.
                  </div>
                ) : statusManagementData.map((status, idx) => (
                  <div 
                    key={status.id || idx}
                    className="settings-table-row status-table-row"
                  >
                    <div>
                      <Chip
                        label={status.status}
                        size="small"
                        className={`chip status ${String(status.status || '')
                          .toLowerCase()
                          .replace(/\s+/g, "-")}`}
                        variant="outlined"
                      />
                    </div>
                    <div>
                      <TextField
                        size="small"
                        fullWidth
                        value={status.customStatus || ''}
                        onChange={(e) => {
                          setStatusManagementData(prev => 
                            prev.map(s => 
                              s.id === status.id 
                                ? { ...s, customStatus: e.target.value } 
                                : s
                            )
                          );
                        }}
                        placeholder=""
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                        }}                       
                      />
                    </div>
                    <div>
                      <TextField
                        size="small"
                        fullWidth
                        value={status.description || ''}
                        onChange={(e) => {
                          setStatusManagementData(prev => 
                            prev.map(s => 
                              s.id === status.id 
                                ? { ...s, description: e.target.value } 
                                : s
                            )
                          );
                        }}
                        placeholder=""
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                        }}                        
                      />
                    </div>
                    <div className="visibility-cell">
                      {status.status?.toLowerCase() === 'on hold' && (
                        <Button
                          size="large"
                          color="inherit"
                          startIcon={status.isVisible ? <ToggleOnIcon sx={{ fontSize: '36px !important', color: '#00a3e0' }} /> : <ToggleOffIcon sx={{ fontSize: '36px !important', color: '#666666' }} />}
                          onClick={() => {
                            setStatusManagementData(prev => 
                              prev.map(s => 
                                s.id === status.id 
                                  ? { ...s, isVisible: !s.isVisible } 
                                  : s
                              )
                            );
                          }}
                          sx={{ minWidth: 'auto', padding: 0 }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions className="project-settings-dialog-actions">
          <Button 
            variant="outlined" 
            onClick={handleCancelSettings} 
            disabled={savingProjectSettings || savingStatusData}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleOpenResetProjectSettingsConfirm}
            disabled={savingProjectSettings || savingStatusData || resettingProjectSettings}
          >
            Reset Project Settings
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveAllSettings}
            disabled={(!isSettingsDirty && !isStatusDirty) || savingProjectSettings || savingStatusData}
          >
            {(savingProjectSettings || savingStatusData) ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Reset Project Settings dialog */}
      <Dialog
        open={confirmResetProjectSettingsOpen}
        onClose={handleCloseResetProjectSettingsConfirm}
      >
        <DialogTitle>Confirm Reset? </DialogTitle>
        <DialogContent>
          <div className="confirm-exit-message">
            Are you sure you want to Reset Project Setting.
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCloseResetProjectSettingsConfirm}
            disabled={resettingProjectSettings}
            sx={{ textTransform: "none" }}
          >
            No
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmResetProjectSettings}
            disabled={resettingProjectSettings}
            sx={{ textTransform: "none" }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Exit Settings dialog */}
      <Dialog
        open={confirmExitSettingsOpen}
        onClose={() => setConfirmExitSettingsOpen(false)}
      >
        <DialogTitle>Are you sure you want to cancel?</DialogTitle>
        <DialogContent>         
          <div className="confirm-exit-message">
            You have unsaved changes. Leaving now will discard them.
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleConfirmExitSettings}
            sx={{ textTransform: "none" }}
          >
            Exit
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveAllSettings}
            sx={{ textTransform: "none" }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exit Project Page? dialog - shown when the user clicks the Projects Cancel button */}
      <Dialog
        open={confirmCancelGridOpen}
        onClose={() => setConfirmCancelGridOpen(false)}
        sx={{ "& .MuiDialog-container": { alignItems: "flex-start" }, "& .MuiDialog-paper": { marginTop: "80px" } }}
      >
        <DialogTitle className="confirm-cancel-dialog-title">
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <WarningAmberIcon sx={{ color: "#ed6c02" }} />
            <span>Exit Project Page?</span>
          </Stack>
        </DialogTitle>
        <div className="confirm-cancel-dialog-divider" />
        <DialogContent>
          <div className="confirm-exit-message">
            Are you sure you want to exit?
            <br /><br />
            You have unsaved changes. Leaving now will discard them and changes made will not be reflected in the client view.
            <br /><br />
            <ul>
              <li>Select 'Save Changes' to save edits and update the client view.</li>
              <li>Select 'Exit' to leave and discard changes.</li>
            </ul>
          </div>
        </DialogContent>
        <DialogActions className="confirm-cancel-dialog-actions">
          <Button
            variant="outlined"
            onClick={handleConfirmCancelGridExit}
            disabled={savingChanges}
            sx={{ textTransform: "none" }}
          >
            Exit
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => { setConfirmCancelGridOpen(false); handleSave(); }}
            disabled={savingChanges}
            sx={{ textTransform: "none" }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Exit Tasks Page? dialog - shown when the user clicks the Tasks Cancel button */}
      <Dialog
        open={confirmCancelTasksOpen}
        onClose={() => setConfirmCancelTasksOpen(false)}
        sx={{ "& .MuiDialog-container": { alignItems: "flex-start" }, "& .MuiDialog-paper": { marginTop: "80px" } }}
      >
        <DialogTitle className="confirm-cancel-dialog-title">
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <WarningAmberIcon sx={{ color: "#ed6c02" }} />
            <span>Exit Tasks Page?</span>
          </Stack>
        </DialogTitle>
        <div className="confirm-cancel-dialog-divider" />
        <DialogContent>
          <div className="confirm-exit-message">
            Are you sure you want to exit?
            <br /><br />
            You have unsaved changes. Leaving now will discard them and changes made will not be reflected in the client view.
            <br /><br />
            <ul>
              <li>Select 'Save Changes' to save edits and update the client view.</li>
              <li>Select 'Exit' to leave and discard changes.</li>
            </ul>
          </div>
        </DialogContent>
        <DialogActions className="confirm-cancel-dialog-actions">
          <Button
            variant="outlined"
            onClick={() => { setConfirmCancelTasksOpen(false); tasksCancelRef.current?.(); }}
            disabled={tasksIsSaving}
            sx={{ textTransform: "none" }}
          >
            Exit
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => { setConfirmCancelTasksOpen(false); handleSave(); }}
            disabled={tasksIsSaving}
            sx={{ textTransform: "none" }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      <ProjectVisibilityConfirmDialogs
        confirmShowOpen={confirmShowDialogOpen}
        confirmHideOpen={confirmHideDialogOpen}
        saving={savingVisibility}
        onCancelShow={handleCancelShowProjects}
        onConfirmShow={handleConfirmShowProjects}
        onCancelHide={handleCancelHideProjects}
        onConfirmHide={handleConfirmHideProjects}
      />

      {/* Switch to Client View with unsaved changes confirmation */}
      <Dialog
        open={confirmSwitchClientViewOpen}
        onClose={() => setConfirmSwitchClientViewOpen(false)}
        sx={{ alignItems: "flex-start", justifyContent: "center", "& .MuiDialog-container": { alignItems: "flex-start" }, "& .MuiDialog-paper": { marginTop: "80px" } }}
      >
        <DialogTitle>
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <WarningAmberIcon sx={{ color: "#ed6c02" }} />
            <span>Unsaved Changes</span>
          </Stack>
        </DialogTitle>
        <div className="confirm-cancel-dialog-divider" />
        <DialogContent>
          <div className="confirm-exit-message">
            You have unsaved changes. They will not be visible in the Client View. Please save your
            changes before continuing.
          </div>
        </DialogContent>
        <DialogActions className="confirm-cancel-dialog-actions">
          <Button
            variant="outlined"
            onClick={() => setConfirmSwitchClientViewOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmSwitchClientView}
            sx={{ textTransform: "none" }}
          >
            Continue to Client View
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave-page guard for SPA navigation (browser back/forward button) */}
      <Dialog
        open={confirmLeavePageOpen}
        onClose={handleCancelLeavePage}
        sx={{ alignItems: "flex-start", justifyContent: "center", "& .MuiDialog-container": { alignItems: "flex-start" }, "& .MuiDialog-paper": { marginTop: "80px" } }}
      >
        <DialogTitle>
          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
            <WarningAmberIcon sx={{ color: "#ed6c02" }} />
            <span>Leave Page?</span>
          </Stack>
        </DialogTitle>
        <div className="confirm-cancel-dialog-divider" />
        <DialogContent>
          <div className="confirm-exit-message">
            You have unsaved changes. Are you sure you want to leave this page?
            <br /><br />
            Your unsaved changes will be discarded.
          </div>
        </DialogContent>
        <DialogActions className="confirm-cancel-dialog-actions">
          <Button
            variant="outlined"
            onClick={handleCancelLeavePage}
            sx={{ textTransform: "none" }}
          >
            Stay on Page
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmLeavePage}
            sx={{ textTransform: "none" }}
          >
            Leave & Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      <Popper
        open={isEngagementHoverOpen && !!engagementHoverAnchorEl}
        anchorEl={engagementHoverAnchorEl}
        placement="bottom-start"
        className="engagement-hover-popper"
        modifiers={[
          { name: "offset", options: { offset: [0, 8] } },
          { name: "preventOverflow", options: { padding: 8 } },
          { name: "flip", options: { padding: 8 } },
        ]}
      >
        <div
          ref={engagementHoverCardRef}
          className="engagement-hover-card"
          onMouseEnter={keepEngagementHoverOpen}
          onMouseLeave={scheduleCloseEngagementHover}
        >
          <div className="engagement-hover-list">
            {engagementHoverManagers.map((manager, idx) => (
              <div
                className="engagement-hover-item"
                key={`${manager?.id ?? manager?.name ?? idx}-${idx}`}
              >
                <span className="engagement-hover-avatar">
                  {getInitials(manager?.name || "")}
                </span>
                <div className="engagement-hover-text">
                  <div className="engagement-hover-name">
                    {manager?.name || "-"}
                  </div>
                  <div
                    className="engagement-hover-email engagement-email-copy"
                    title={manager?.email ? "Click to copy" : ""}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      copyEmailToClipboard(manager?.email || "");
                    }}
                  >
                    {manager?.email || ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div
            className="engagement-hover-view-all"
            onClick={handleOpenEngagementManagersFromHover}
          >
            View All
          </div>
        </div>
      </Popper>

      <Dialog
        open={isEngagementManagersOpen}
        onClose={() => setIsEngagementManagersOpen(false)}
        maxWidth="sm"
        className="engagement-managers-dialog"
      >
        <DialogTitle className="engagement-dialog-title">
          <PeopleAltOutlinedIcon className="engagement-dialog-title-icon" />
          <span>Engagement Managers</span>
        </DialogTitle>
        <DialogContent>
          <div className="engagement-dialog-list">
            {engagementManagersForDialog.map((manager, index) => (
              <div
                className="engagement-dialog-item"
                key={`${manager?.id ?? manager?.name ?? index}-${index}`}
              >
                <Chip
                  label={getInitials(manager?.name || "")}
                  size="small"
                  className="engagement-chip"
                />
                <div className="engagement-dialog-text">
                  <div className="engagement-dialog-name">
                    {manager?.name || "-"}
                  </div>
                  <div
                    className="engagement-dialog-email engagement-email-copy"
                    title={manager?.email ? "Click to copy" : ""}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      copyEmailToClipboard(manager?.email || "");
                    }}
                  >
                    {manager?.email || ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            size="small"
            className="engagement-dialog-close-btn"
            onClick={() => setIsEngagementManagersOpen(false)}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Messages Dialog */}
      {messagesProject ? (
        <MessagesDialog
          open={isMessagesOpen}
          onClose={handleCloseMessages}
          projectName={messagesProject.project}
          entity={messagesProject.client}
          projectIdMdm={messagesProject.projectIdMdm || 0}
          siteId={ parseInt(sessionStorage.getItem("siteId")!)}
          currentUserId={user?.profile.name || "UserName"}
          currentUserEmail={user?.profile.preferred_username || "UserEmail"}
          siteUsers={siteUsers}
          canSendMessage={!isHiddenEngagementManagerForProject(messagesProject)}
        />
      ) : null}

      {/* Status Details Dialog */}
      {statusDetailsProject ? (
        <StatusDetailsDialog
          open={isStatusDetailsOpen}
          onClose={handleCloseStatusDetails}
          projectName={statusDetailsProject.project}
          projectIdMdm={statusDetailsProject.projectIdMdm || 0}
          siteId={parseInt(sessionStorage.getItem("siteId")!)}
          isClientView={isClientView}
          canUpdateNotes={!isHiddenEngagementManagerForProject(statusDetailsProject)}
          currentStatus={statusDetailsProject.status}
          onStatusUpdate={handleStatusUpdate}
        />
      ) : null}

      {/* Project Details Dialog */}
      {projectDetailsProject ? (
        <ProjectDetailsDialog
          open={isProjectDetailsOpen}
          onClose={handleCloseProjectDetails}
          projectDefaultName={projectDetailsProject.projectDefaultName}
          projectCustomName={projectDetailsProject.projectCustomName}
          projectIdMdm={projectDetailsProject.projectIdMdm}
          siteId={parseInt(sessionStorage.getItem("siteId")!)}
          engagementManagers={projectDetailsProject.engagementManagers}
          entityId={projectDetailsProject.clientId}
          entityName={projectDetailsProject.client}
          onUpdate={handleProjectDetailsUpdate}
        />
      ) : null}
    </div>
    </>
  );
};

export default withTimCompactTheme(ProjectsView);
